import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    Search,
    Image,
    Video,
    Link,
    CheckCircle2,
    CalendarIcon,
    MapPin,
    Target,
    Clock,
    Package,
    Tv,
    ArrowRight,
    ArrowLeft,
    Sparkles,
    X,
    ChevronDown,
    ChevronUp,
    Plus,
    List,
    Store,
    AlertCircle,
    Pencil,
    Trash2,
    Car,
    Building2,
    Info,
    Users,
    Timer,
    Eye,
    Loader2,
    ZoomIn,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';

interface Media {
    id: string;
    title: string;
    description?: string | null;
    url: string;
    type: string;
    display_time: number;
    created_at: string;
}

interface WatchAdsModalProps {
    open: boolean;
    onClose: () => void;
    medias: Media[];
}

// Interface para campanhas
interface Campaign {
    id: string;
    name: string;
    description: string;
    cities: string[];
    startDate: string;
    endDate: string;
    mediaCount: number;
    status: 'active' | 'paused' | 'ended' | 'scheduled' | 'pending';
    targetMobile: boolean;  // Veículos de frota
    targetFixed: boolean;   // Pontos físicos
    createdAt: string;
    impressions?: number;
    clicks?: number;
}

// Interface para parceiros Venue
interface VenuePartner {
    id: string;
    user_id: string;
    venue_name: string;
    venue_type: string;
    venue_cep: string;
    venue_street: string;
    venue_number: string;
    venue_complement: string | null;
    venue_neighborhood: string;
    venue_city: string;
    venue_state: string;
    venue_photos: string[];
    screen_quantity: number;
    daily_footfall: number | null;
    average_retention: number | null;
    blacklisted_categories: string[];
    custom_blacklist_brands: string[];
    status: 'pending' | 'approved' | 'rejected' | 'active' | 'inactive';
    created_at: string;
    active_device_count?: number; // Contagem real de dispositivos ativos
}

// Interface para campanhas externas (anúncios venue)
interface VenueCampaign {
    id: string;
    advertiser_user_id: string;
    partner_user_id: string;
    venue_partner_id: string;
    media_ids: string[];
    campaign_name: string;
    campaign_description?: string;
    start_date: string;
    end_date: string;
    target_mobile: boolean;
    target_fixed: boolean;
    ads_status: 'active' | 'inactive' | 'pending' | 'approved' | 'rejected' | 'ended' | 'correction';
    admin_comment?: string | null;
    created_at: string;
    updated_at: string;
    // Dados do parceiro via join (opcional)
    venue_partner?: {
        id: string;
        venue_name: string;
        venue_city: string;
        venue_state: string;
        venue_type: string;
        screen_quantity: number;
        venue_photos: string[];
    };
}

// Mapear tipos de venue para labels amigáveis
const VENUE_TYPE_LABELS: Record<string, string> = {
    'academia': 'Academia',
    'restaurante': 'Restaurante',
    'bar': 'Bar',
    'cafe': 'Cafeteria',
    'clinica': 'Clínica',
    'hospital': 'Hospital',
    'hotel': 'Hotel',
    'shopping': 'Shopping',
    'mercado': 'Supermercado',
    'loja': 'Loja',
    'escritorio': 'Escritório',
    'coworking': 'Coworking',
    'salao': 'Salão de Beleza',
    'barbearia': 'Barbearia',
    'padaria': 'Padaria',
    'farmacia': 'Farmácia',
    'posto': 'Posto de Combustível',
    'outro': 'Outro',
};

// Cidades disponíveis - Grande Florianópolis e Litoral Norte SC
const AVAILABLE_CITIES = [
    { value: 'florianopolis', label: 'Florianópolis (Capital)' },
    { value: 'sao-jose', label: 'São José' },
    { value: 'palhoca', label: 'Palhoça' },
    { value: 'biguacu', label: 'Biguaçu' },
    { value: 'santo-amaro-imperatriz', label: 'Santo Amaro da Imperatriz' },
    { value: 'governador-celso-ramos', label: 'Governador Celso Ramos' },
    { value: 'antonio-carlos', label: 'Antônio Carlos' },
    { value: 'aguas-mornas', label: 'Águas Mornas' },
    { value: 'sao-pedro-alcantara', label: 'São Pedro de Alcântara' },
    { value: 'balneario-camboriu', label: 'Balneário Camboriú' },
    { value: 'itapema', label: 'Itapema' },
];

// Interface para campanhas Urban do banco de dados
interface UrbanCampaign {
    id: string;
    advertiser_user_id: string;
    campaign_name: string;
    campaign_description: string | null;
    media_ids: string[];
    circulation_cities: string[];
    start_date: string;
    end_date: string;
    target_mobile: boolean;
    target_fixed: boolean;
    status: 'pending' | 'active' | 'paused' | 'ended' | 'rejected' | 'correction' | 'approved';
    admin_comment: string | null;
    total_impressions: number;
    total_clicks: number;
    total_audited_trips: number;
    created_at: string;
    updated_at: string;
}

const WatchAdsModal = ({ open, onClose, medias }: WatchAdsModalProps) => {
    const { toast } = useToast();

    // Estados para configurações de tipos de veiculação (carregados do admin)
    const [adsUrbanEnabled, setAdsUrbanEnabled] = useState(true);
    const [adsVenueEnabled, setAdsVenueEnabled] = useState(true);

    // Tab ativa: 'create' ou 'campaigns'
    const [activeTab, setActiveTab] = useState<'create' | 'campaigns'>('create');

    // Estados do formulário (tab criar)
    // Step 1: Escolha tipo de veiculação, Step 2: Selecionar mídias (ADS Urban), Step 3: Configurar pack
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [adsServiceType, setAdsServiceType] = useState<'urban' | 'venue' | null>(null); // Tipo de serviço ADS selecionado
    const [hasChosenService, setHasChosenService] = useState(false); // Indica se já passou pelo step de escolha
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);

    // Configuração do pack
    const [packName, setPackName] = useState('');
    const [packDescription, setPackDescription] = useState('');
    const [selectedCities, setSelectedCities] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Date | undefined>(new Date());
    const [endDate, setEndDate] = useState<Date | undefined>(addDays(new Date(), 7));
    const [isStartOpen, setIsStartOpen] = useState(false);
    const [isEndOpen, setIsEndOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCitiesExpanded, setIsCitiesExpanded] = useState(false);
    const [isPeriodExpanded, setIsPeriodExpanded] = useState(false);
    const [isDescExpanded, setIsDescExpanded] = useState(false);

    // Estados para targeting da nova campanha
    const [targetMobile, setTargetMobile] = useState(true);
    const [targetFixed, setTargetFixed] = useState(false);

    // Estados da tab campanhas
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);
    const [isCampaignsListExpanded, setIsCampaignsListExpanded] = useState(true);
    const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);

    // Estados para campanhas Urban do banco de dados
    const [urbanCampaigns, setUrbanCampaigns] = useState<UrbanCampaign[]>([]);
    const [loadingUrbanCampaigns, setLoadingUrbanCampaigns] = useState(false);

    // Estado para edição de campanha
    const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
    const [editingUrbanCampaign, setEditingUrbanCampaign] = useState<UrbanCampaign | null>(null);
    const [urbanCampaignToDelete, setUrbanCampaignToDelete] = useState<UrbanCampaign | null>(null);

    // Estados para ADS Venue - Parceiros
    const [venuePartners, setVenuePartners] = useState<VenuePartner[]>([]);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState<VenuePartner | null>(null);
    const [partnerSearchQuery, setPartnerSearchQuery] = useState('');
    const [selectedCityFilter, setSelectedCityFilter] = useState<string | null>(null);

    // Estados para o carrossel de imagens do parceiro
    const [photoCarouselOpen, setPhotoCarouselOpen] = useState(false);
    const [carouselPhotos, setCarouselPhotos] = useState<string[]>([]);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [carouselPartnerName, setCarouselPartnerName] = useState('');

    // Estado para o usuário logado (para filtrar parceiros próprios)
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);

    // Estados para etapa 3 - Seleção do parceiro e criação de campanha externa
    const [isCreatingVenueCampaign, setIsCreatingVenueCampaign] = useState(false);
    const [venueCampaignData, setVenueCampaignData] = useState<VenueCampaign | null>(null);
    const [isExternalCampaign, setIsExternalCampaign] = useState(false);

    // Estados para campanhas Venue do banco de dados
    const [venueCampaigns, setVenueCampaigns] = useState<VenueCampaign[]>([]);
    const [loadingVenueCampaigns, setLoadingVenueCampaigns] = useState(false);
    const [editingVenueCampaign, setEditingVenueCampaign] = useState<VenueCampaign | null>(null);
    const [closedNotifications, setClosedNotifications] = useState<string[]>([]);

    // Estado para visualização de mídia (Modal sobreposto)
    const [viewingMedia, setViewingMedia] = useState<Media | null>(null);

    // Estados para modal de seleção de tempo de exibição (ADS Venue)
    const [displayTimeModalOpen, setDisplayTimeModalOpen] = useState(false);
    const [pendingMediaId, setPendingMediaId] = useState<string | null>(null);
    const [mediaDisplayTimes, setMediaDisplayTimes] = useState<Record<string, number>>({});

    // Reset ao fechar
    useEffect(() => {
        if (!open) {
            setActiveTab('create');
            setStep(1);
            setSearchQuery('');
            setSelectedMediaIds([]);
            setPackName('');
            setPackDescription('');
            setSelectedCities([]);
            setStartDate(new Date());
            setEndDate(addDays(new Date(), 7));
            setIsCitiesExpanded(false);
            setIsPeriodExpanded(false);
            setIsDescExpanded(false);
            setExpandedCampaignId(null);
            setEditingCampaign(null);
            setTargetMobile(true);
            setTargetFixed(false);
            setAdsServiceType(null);
            setHasChosenService(false);
            // Reset estados de parceiros
            setVenuePartners([]);
            setSelectedPartner(null);
            setPartnerSearchQuery('');
            setSelectedCityFilter(null);
            // Reset estados do carrossel
            setPhotoCarouselOpen(false);
            setCarouselPhotos([]);
            setCarouselIndex(0);
            setIsZoomed(false);
            setCarouselPartnerName('');
            // Reset estados de campanha externa
            setIsCreatingVenueCampaign(false);
            setVenueCampaignData(null);
            setIsExternalCampaign(false);
            // Reset estado de edição de campanha Venue
            setEditingVenueCampaign(null);
            setEditingUrbanCampaign(null);
            setViewingMedia(null);
            // Reset estados do modal de tempo de exibição
            setDisplayTimeModalOpen(false);
            setPendingMediaId(null);
            setMediaDisplayTimes({});
        }
    }, [open]);

    // Carregar configurações de tipos de veiculação do admin
    useEffect(() => {
        const loadAdsSettings = async () => {
            if (!open) return;

            try {
                const { data, error } = await supabase
                    .from('system_settings')
                    .select('ads_urban_enabled, ads_venue_enabled')
                    .single();

                if (!error && data) {
                    setAdsUrbanEnabled(data.ads_urban_enabled ?? true);
                    setAdsVenueEnabled(data.ads_venue_enabled ?? true);
                }
            } catch (err) {
                console.error('Erro ao carregar configurações de ADS:', err);
            }
        };

        loadAdsSettings();
    }, [open]);

    // Carregar parceiros ativos do banco quando modal abre (excluindo os do próprio usuário)
    useEffect(() => {
        const loadVenuePartners = async () => {
            if (!open) return;

            setLoadingPartners(true);
            try {
                // Obter o usuário logado
                const { data: { user } } = await supabase.auth.getUser();
                const userId = user?.id || null;
                setCurrentUserId(userId);

                // Carregar parceiros ativos
                console.log('🔍 [WatchAdsModal] Buscando parceiros com status active...');
                const { data, error } = await supabase
                    .from('venue_partners')
                    .select('*')
                    .eq('status', 'active')
                    .order('venue_name', { ascending: true });

                console.log('🔍 [WatchAdsModal] Resultado da busca:', { data, error });

                if (error) {
                    console.error('Erro ao carregar parceiros:', error);
                } else {
                    // Filtrar para remover estabelecimentos do próprio usuário
                    const partnersData = (data || []).filter(
                        partner => partner.user_id !== userId
                    );

                    // Buscar contagem real de dispositivos ativos para cada parceiro
                    const userIds = partnersData.map(p => p.user_id).filter(Boolean) as string[];

                    if (userIds.length > 0) {
                        try {
                            const { data: devicesData, error: devicesError } = await supabase
                                .from('devices')
                                .select('user_id')
                                .in('user_id', userIds)
                                .eq('status', 'active');

                            if (!devicesError && devicesData) {
                                const deviceCounts: Record<string, number> = {};
                                devicesData.forEach((d: any) => {
                                    if (d.user_id) {
                                        deviceCounts[d.user_id] = (deviceCounts[d.user_id] || 0) + 1;
                                    }
                                });

                                // Atualizar parceiros com a contagem real
                                partnersData.forEach(p => {
                                    if (p.user_id) {
                                        p.active_device_count = deviceCounts[p.user_id] || 0;
                                    }
                                });
                            }
                        } catch (countError) {
                            console.error('❌ [WatchAdsModal] Erro ao contar dispositivos:', countError);
                        }
                    }

                    console.log('🔍 [WatchAdsModal] Parceiros encontrados:', partnersData.length);
                    setVenuePartners(partnersData);
                }
            } catch (err) {
                console.error('Erro inesperado ao carregar parceiros:', err);
            } finally {
                setLoadingPartners(false);
            }
        };

        loadVenuePartners();
    }, [open]);

    // Carregar campanhas Venue do usuário logado (Memoizado para ser chamado manualmente)
    const loadVenueCampaigns = useCallback(async () => {
        if (!currentUserId) return;

        setLoadingVenueCampaigns(true);
        try {
            console.log('📦 [WatchAdsModal] Buscando campanhas Venue do usuário:', currentUserId);
            const { data, error } = await supabase
                .from('venue_campaigns')
                .select(`
                    *,
                    venue_partner:venue_partners (
                        id,
                        venue_name,
                        venue_city,
                        venue_state,
                        venue_type,
                        screen_quantity,
                        venue_photos
                    )
                `)
                .eq('advertiser_user_id', currentUserId)
                .order('created_at', { ascending: false });

            console.log('📦 [WatchAdsModal] Resultado das campanhas:', { data, error });

            if (error) {
                console.error('Erro ao carregar campanhas Venue:', error);
            } else {
                setVenueCampaigns(data || []);
            }
        } catch (err) {
            console.error('Erro inesperado ao carregar campanhas Venue:', err);
        } finally {
            setLoadingVenueCampaigns(false);
        }
    }, [currentUserId]);

    // Efeito para carregar ao abrir ou mudar usuário
    useEffect(() => {
        if (open) {
            loadVenueCampaigns();
        }
    }, [open, loadVenueCampaigns]);

    // Carregar campanhas Urban do usuário logado
    const loadUrbanCampaigns = useCallback(async () => {
        if (!currentUserId) return;

        setLoadingUrbanCampaigns(true);
        try {
            console.log('🚗 [WatchAdsModal] Buscando campanhas Urban do usuário:', currentUserId);
            const { data, error } = await supabase
                .from('urban_campaigns')
                .select('*')
                .eq('advertiser_user_id', currentUserId)
                .order('created_at', { ascending: false });

            console.log('🚗 [WatchAdsModal] Resultado das campanhas Urban:', { data, error });

            if (error) {
                console.error('Erro ao carregar campanhas Urban:', error);
            } else {
                setUrbanCampaigns(data || []);
            }
        } catch (err) {
            console.error('Erro inesperado ao carregar campanhas Urban:', err);
        } finally {
            setLoadingUrbanCampaigns(false);
        }
    }, [currentUserId]);

    // Efeito para carregar campanhas Urban ao abrir
    useEffect(() => {
        if (open && currentUserId) {
            loadUrbanCampaigns();
        }
    }, [open, currentUserId, loadUrbanCampaigns]);

    // Filtrar parceiros pela busca e cidade
    const filteredPartners = useMemo(() => {
        let filtered = venuePartners;

        // Filtrar por cidade selecionada
        if (selectedCityFilter) {
            filtered = filtered.filter(partner => {
                const city = partner.venue_city.toLowerCase();
                // Agrupar regiões
                if (selectedCityFilter === 'florianopolis') {
                    // Florianópolis e região metropolitana
                    return city.includes('florianópolis') ||
                        city.includes('florianopolis') ||
                        city.includes('são josé') ||
                        city.includes('sao jose') ||
                        city.includes('palhoça') ||
                        city.includes('palhoca') ||
                        city.includes('biguaçu') ||
                        city.includes('biguacu');
                } else if (selectedCityFilter === 'litoral') {
                    // Litoral Norte - Balneário Camboriú e região
                    return city.includes('balneário camboriú') ||
                        city.includes('balneario camboriu') ||
                        city.includes('itapema') ||
                        city.includes('camboriú') ||
                        city.includes('camboriu') ||
                        city.includes('itajaí') ||
                        city.includes('itajai') ||
                        city.includes('navegantes');
                }
                return true;
            });
        }

        // Filtrar por busca de texto
        if (partnerSearchQuery.trim()) {
            const query = partnerSearchQuery.toLowerCase();
            filtered = filtered.filter(
                partner =>
                    partner.venue_name.toLowerCase().includes(query) ||
                    partner.venue_city.toLowerCase().includes(query) ||
                    partner.venue_type.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [venuePartners, partnerSearchQuery, selectedCityFilter]);

    // Toggle cidade
    const handleToggleCity = (cityValue: string) => {
        setSelectedCities(prev =>
            prev.includes(cityValue)
                ? prev.filter(c => c !== cityValue)
                : [...prev, cityValue]
        );
    };

    // Selecionar todas as cidades
    const handleSelectAllCities = () => {
        if (selectedCities.length === AVAILABLE_CITIES.length) {
            setSelectedCities([]);
        } else {
            setSelectedCities(AVAILABLE_CITIES.map(c => c.value));
        }
    };

    // Filtrar mídias
    const filteredMedias = useMemo(() => {
        if (!searchQuery.trim()) return medias;
        const query = searchQuery.toLowerCase();
        return medias.filter(
            media =>
                media.title.toLowerCase().includes(query) ||
                media.description?.toLowerCase().includes(query)
        );
    }, [medias, searchQuery]);

    // Toggle seleção - para ADS Venue, abre modal de tempo primeiro
    const handleToggleMedia = (mediaId: string) => {
        // Se já está selecionada, apenas remove (sem modal)
        if (selectedMediaIds.includes(mediaId)) {
            setSelectedMediaIds(prev => prev.filter(id => id !== mediaId));
            // Remover também o tempo configurado
            setMediaDisplayTimes(prev => {
                const newTimes = { ...prev };
                delete newTimes[mediaId];
                return newTimes;
            });
            return;
        }

        // Se for ADS Venue, abre o modal de tempo
        if (adsServiceType === 'venue') {
            setPendingMediaId(mediaId);
            setDisplayTimeModalOpen(true);
        } else {
            // Para ADS Urban, toggle normal
            setSelectedMediaIds(prev => [...prev, mediaId]);
        }
    };

    // Confirmar seleção de tempo de exibição (ADS Venue)
    const handleConfirmDisplayTime = (seconds: number) => {
        if (pendingMediaId) {
            // Adicionar mídia à seleção
            setSelectedMediaIds(prev => [...prev, pendingMediaId]);
            // Salvar tempo de exibição configurado
            setMediaDisplayTimes(prev => ({ ...prev, [pendingMediaId]: seconds }));
        }
        // Fechar modal e limpar pendência
        setDisplayTimeModalOpen(false);
        setPendingMediaId(null);
    };

    // Cancelar modal de tempo
    const handleCancelDisplayTime = () => {
        setDisplayTimeModalOpen(false);
        setPendingMediaId(null);
    };

    // Selecionar todas
    const handleSelectAll = () => {
        if (selectedMediaIds.length === filteredMedias.length) {
            setSelectedMediaIds([]);
        } else {
            setSelectedMediaIds(filteredMedias.map(m => m.id));
        }
    };


    // Abrir carrossel de fotos do parceiro
    const openPhotoCarousel = (photos: string[], partnerName: string, startIndex: number = 0) => {
        setCarouselPhotos(photos);
        setCarouselPartnerName(partnerName);
        setCarouselIndex(startIndex);
        setIsZoomed(false);
        setPhotoCarouselOpen(true);
    };

    // Navegar no carrossel
    const navigateCarousel = (direction: 'prev' | 'next') => {
        setIsZoomed(false);
        if (direction === 'prev') {
            setCarouselIndex(prev => (prev === 0 ? carouselPhotos.length - 1 : prev - 1));
        } else {
            setCarouselIndex(prev => (prev === carouselPhotos.length - 1 ? 0 : prev + 1));
        }
    };

    // Fechar carrossel
    const closePhotoCarousel = () => {
        setPhotoCarouselOpen(false);
        setIsZoomed(false);
    };

    // Calcular duração
    const duration = startDate && endDate ? differenceInDays(endDate, startDate) + 1 : 0;

    // Obter thumbnail
    const getThumbnailUrl = (media: Media): string => {
        const url = media.url.toLowerCase();
        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
        if (youtubeMatch) {
            return `https://img.youtube.com/vi/${youtubeMatch[1]}/mqdefault.jpg`;
        }
        if (url.includes('vimeo.com')) {
            return 'https://via.placeholder.com/320x180/6366f1/ffffff?text=Vimeo';
        }
        if (url.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
            return media.url;
        }
        return 'https://via.placeholder.com/320x180/10b981/ffffff?text=Mídia';
    };

    // Ícone do tipo de mídia
    const getMediaTypeIcon = (type: string, url: string) => {
        const lowUrl = url.toLowerCase();
        if (type === 'video' || lowUrl.includes('youtube') || lowUrl.includes('vimeo') || lowUrl.match(/\.(mp4|webm|mov)$/)) {
            return <Video className="h-3.5 w-3.5 text-purple-500" />;
        }
        if (lowUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) {
            return <Image className="h-3.5 w-3.5 text-blue-500" />;
        }
        return <Link className="h-3.5 w-3.5 text-gray-500" />;
    };

    // Validar step 1
    const canProceedToStep2 = selectedMediaIds.length > 0;

    // Validar formulário - para Urban requer cidades, para Venue não (localização determinada pelo parceiro)
    const canSubmit = adsServiceType === 'venue'
        ? packName.trim() && startDate && endDate && selectedMediaIds.length > 0 && selectedPartner !== null
        : packName.trim() && selectedCities.length > 0 && startDate && endDate && selectedMediaIds.length > 0;

    // Submeter pack
    const handleSubmit = async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);

        // Se for campanha externa (Venue), exibir notificação, atualizar lista e ir para Minhas Campanhas
        if (isExternalCampaign && venueCampaignData) {
            await loadVenueCampaigns(); // Atualiza a lista com a nova campanha

            toast({
                title: 'Campanha criada e enviada para aprovação',
            });
            setIsSubmitting(false);
            // Redirecionar para a tab de campanhas para o usuário ver o status
            setActiveTab('campaigns');
            return;
        }

        // ============================================================
        // ADS URBAN - Salvar na tabela urban_campaigns
        // ============================================================
        if (adsServiceType === 'urban') {
            try {
                // Obter usuário logado
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) {
                    toast({
                        title: 'Erro de autenticação',
                        description: 'Faça login novamente para criar a campanha.',
                        variant: 'destructive',
                    });
                    setIsSubmitting(false);
                    return;
                }

                // Preparar dados da campanha Urban
                const urbanCampaignData = {
                    advertiser_user_id: user.id,
                    campaign_name: packName,
                    campaign_description: packDescription || null,
                    media_ids: selectedMediaIds,
                    circulation_cities: selectedCities,
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                    target_mobile: targetMobile,
                    target_fixed: targetFixed,
                    status: 'pending',
                };

                console.log('📦 [ADS Urban] Criando campanha:', urbanCampaignData);

                // Salvar no banco de dados
                const { data, error } = await supabase
                    .from('urban_campaigns')
                    .insert(urbanCampaignData)
                    .select()
                    .single();

                if (error) {
                    console.error('❌ [ADS Urban] Erro ao criar campanha:', error);
                    toast({
                        title: 'Erro ao criar campanha',
                        description: error.message || 'Ocorreu um erro ao criar sua campanha. Tente novamente.',
                        variant: 'destructive',
                    });
                    setIsSubmitting(false);
                    return;
                }

                console.log('✅ [ADS Urban] Campanha criada:', data);

                // Atualizar lista de campanhas
                await loadUrbanCampaigns();

                toast({
                    title: 'Campanha ADS Urban criada!',
                    description: `"${packName}" foi enviada para aprovação. Você pode acompanhar o status em "Minhas Campanhas".`,
                });

                setIsSubmitting(false);
                // Redirecionar para a tab de campanhas para o usuário ver o status
                setActiveTab('campaigns');
                return;

            } catch (err) {
                console.error('❌ [ADS Urban] Erro inesperado:', err);
                toast({
                    title: 'Erro inesperado',
                    description: 'Ocorreu um erro ao criar sua campanha. Tente novamente.',
                    variant: 'destructive',
                });
                setIsSubmitting(false);
                return;
            }
        }

        // Fallback para outros tipos (legacy)
        const packData = {
            name: packName,
            description: packDescription,
            media_ids: selectedMediaIds,
            cities: selectedCities,
            start_date: startDate?.toISOString(),
            end_date: endDate?.toISOString(),
            status: 'pending',
            created_at: new Date().toISOString(),
        };

        console.log('📦 Watch Ads - Criando pack (legacy):', packData);

        await new Promise(resolve => setTimeout(resolve, 1500));

        toast({
            title: 'Pack criado com sucesso!',
            description: `"${packName}" com ${selectedMediaIds.length} mídia(s) está pronto para ser disponibilizado aos motoristas.`,
        });

        setIsSubmitting(false);
        onClose();
    };

    // Obter labels das cidades selecionadas
    const getSelectedCitiesLabels = () => {
        return selectedCities.map(value =>
            AVAILABLE_CITIES.find(c => c.value === value)?.label || value
        );
    };

    // Helpers para campanhas
    const getCityLabel = (cityValue: string) => {
        return AVAILABLE_CITIES.find(c => c.value === cityValue)?.label || cityValue;
    };

    const getStatusConfig = (status: Campaign['status'] | string) => {
        switch (status) {
            case 'active':
                return { label: 'Em veiculação', color: 'bg-green-100 text-green-700 border-green-200' };
            case 'ended':
                return { label: 'Finalizada', color: 'bg-gray-100 text-gray-600 border-gray-200' };
            case 'inactive':
            case 'pending':
                return { label: 'Aguardando aprovação', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
            case 'paused':
                return { label: 'Pausada', color: 'bg-gray-100 text-gray-700 border-gray-200' };
            case 'correction':
                return { label: 'Correção Solicitada', color: 'bg-orange-100 text-orange-800 border-orange-200' };
            case 'approved':
                return { label: 'Aprovada', color: 'bg-green-100 text-green-700 border-green-200' };
            case 'rejected':
                return { label: 'Rejeitada', color: 'bg-red-100 text-red-700 border-red-200' };
            default:
                return { label: status, color: 'bg-gray-100 text-gray-600 border-gray-200' };
        }
    };

    const handleDeleteCampaign = (campaign: Campaign) => {
        // Abre o diálogo de confirmação
        setCampaignToDelete(campaign);
    };

    const confirmDeleteCampaign = () => {
        if (campaignToDelete) {
            setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete.id));
            toast({
                title: 'Campanha excluída',
                description: `"${campaignToDelete.name}" foi removida com sucesso.`,
            });
            setCampaignToDelete(null);
        }
    };

    const cancelDeleteCampaign = () => {
        setCampaignToDelete(null);
    };

    const handleSelectVenuePartner = async (partner: VenuePartner) => {
        if (!partner || !selectedMediaIds.length) {
            toast({
                title: 'Erro',
                description: 'Por favor, selecione pelo menos uma mídia antes de escolher um parceiro.',
                variant: 'destructive',
            });
            return;
        }

        setIsCreatingVenueCampaign(true);

        try {
            // Obter dados do usuário logado
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                toast({
                    title: 'Erro',
                    description: 'Você precisa estar logado para criar uma campanha.',
                    variant: 'destructive',
                });
                setIsCreatingVenueCampaign(false);
                return;
            }

            // Preparar dados da campanha externa (cities não é necessário - localização determinada pelo parceiro)
            const campaignData = {
                advertiser_user_id: user.id,
                partner_user_id: partner.user_id,
                venue_partner_id: partner.id,
                media_ids: selectedMediaIds,
                media_display_times: mediaDisplayTimes, // Tempos de exibição configurados pelo anunciante
                campaign_name: packName || `Campanha ${partner.venue_name}`,
                campaign_description: packDescription || `Campanha externa no estabelecimento ${partner.venue_name}`,
                start_date: startDate?.toISOString().split('T')[0],
                end_date: endDate?.toISOString().split('T')[0],
                target_mobile: targetMobile,
                target_fixed: targetFixed,
                ads_status: 'inactive', // Sempre começa como inactive para requerer aprovação
            };

            console.log('🔍 [WatchAdsModal] Criando campanha externa:', campaignData);

            // Salvar no banco de dados
            const { data, error } = await supabase
                .from('venue_campaigns')
                .insert(campaignData)
                .select()
                .single();

            if (error) {
                console.error('Erro ao criar campanha externa:', error);
                toast({
                    title: 'Erro ao criar campanha',
                    description: 'Ocorreu um erro ao criar sua campanha. Tente novamente.',
                    variant: 'destructive',
                });
                return;
            }

            console.log('✅ [WatchAdsModal] Campanha externa criada:', data);

            // Armazenar dados da campanha e parceiro selecionado
            setVenueCampaignData(data);
            setSelectedPartner(partner);

            // Marcar como campanha externa
            setIsExternalCampaign(true);

            // Ir para etapa 4 (confirmação) no fluxo Venue
            setStep(4);

            toast({
                title: 'Ponto de anúncio selecionado',
            });

        } catch (err) {
            console.error('Erro inesperado ao criar campanha:', err);
            toast({
                title: 'Erro',
                description: 'Ocorreu um erro inesperado. Tente novamente.',
                variant: 'destructive',
            });
        } finally {
            setIsCreatingVenueCampaign(false);
        }
    };

    const handleEditCampaign = (campaign: Campaign) => {
        // Preencher o formulário com os dados da campanha
        setEditingCampaign(campaign);
        setPackName(campaign.name);
        setPackDescription(campaign.description);
        setSelectedCities(campaign.cities);
        setStartDate(new Date(campaign.startDate));
        setEndDate(new Date(campaign.endDate));
        setTargetMobile(campaign.targetMobile);
        setTargetFixed(campaign.targetFixed);

        // Mudar para tab de criação no step 2 (configuração)
        setActiveTab('create');
        setStep(2);

        // Expandir todas as seções para visualização
        setIsCitiesExpanded(true);
        setIsPeriodExpanded(true);
        setIsDescExpanded(campaign.description ? true : false);
    };

    const handleSaveEdit = async () => {
        if (!editingCampaign || !packName.trim() || selectedCities.length === 0 || !startDate || !endDate) return;

        setIsSubmitting(true);

        // Atualizar campanha existente
        const updatedCampaign: Campaign = {
            ...editingCampaign,
            name: packName,
            description: packDescription,
            cities: selectedCities,
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            targetMobile,
            targetFixed,
        };

        // Simular delay de API
        await new Promise(resolve => setTimeout(resolve, 1000));

        setCampaigns(prev => prev.map(c => c.id === editingCampaign.id ? updatedCampaign : c));

        toast({
            title: 'Campanha atualizada!',
            description: `"${packName}" foi salva com sucesso.`,
        });

        // Limpar estado de edição e voltar para lista
        setEditingCampaign(null);
        setIsSubmitting(false);
        setActiveTab('campaigns');

        // Resetar formulário
        setPackName('');
        setPackDescription('');
        setSelectedCities([]);
        setStartDate(new Date());
        setEndDate(addDays(new Date(), 7));
        setTargetMobile(true);
        setTargetFixed(false);
        setAdsServiceType(null);
        setHasChosenService(false);
        setSelectedPartner(null);
        setStep(1);
    };

    const handleCancelEdit = () => {
        setEditingCampaign(null);
        setActiveTab('campaigns');

        // Resetar formulário
        setPackName('');
        setPackDescription('');
        setSelectedCities([]);
        setStartDate(new Date());
        setEndDate(addDays(new Date(), 7));
        setTargetMobile(true);
        setTargetFixed(false);
        setAdsServiceType(null);
        setHasChosenService(false);
        setSelectedPartner(null);
        setStep(1);
    };

    const handleToggleCampaignTarget = (campaignId: string, field: 'targetMobile' | 'targetFixed') => {
        setCampaigns(prev => prev.map(c => {
            if (c.id === campaignId) {
                return { ...c, [field]: !c[field] };
            }
            return c;
        }));
    };

    // ========== FUNÇÕES PARA CAMPANHAS VENUE (BANCO DE DADOS) ==========

    // Estado para campanha Venue a ser excluída
    const [venueCampaignToDelete, setVenueCampaignToDelete] = useState<VenueCampaign | null>(null);

    // Editar campanha Venue
    const handleEditVenueCampaign = (campaign: VenueCampaign) => {
        // Bloquear edição se status for active, approved ou ended
        if (['active', 'approved', 'ended'].includes(campaign.ads_status)) {
            toast({
                title: 'Ação não permitida',
                description: 'Apenas campanhas aguardando aprovação ou rejeitadas podem ser editadas.',
                variant: 'destructive',
            });
            return;
        }

        console.log('✏️ [WatchAdsModal] Editando campanha Venue:', campaign);
        setEditingVenueCampaign(campaign);
        setPackName(campaign.campaign_name);
        setPackDescription(campaign.campaign_description || '');
        setSelectedMediaIds(campaign.media_ids.map(id => String(id)));
        // Adicionar T00:00:00 para garantir que seja interpretado como hora local e não UTC
        setStartDate(new Date(campaign.start_date + 'T00:00:00'));
        setEndDate(new Date(campaign.end_date + 'T00:00:00'));
        setTargetMobile(campaign.target_mobile);
        setTargetFixed(campaign.target_fixed);
        setAdsServiceType('venue');
        setHasChosenService(true);

        // Encontrar parceiro associado
        if (campaign.venue_partner) {
            setSelectedPartner({
                id: campaign.venue_partner.id,
                user_id: campaign.partner_user_id,
                venue_name: campaign.venue_partner.venue_name,
                venue_type: campaign.venue_partner.venue_type,
                venue_cep: '',
                venue_street: '',
                venue_number: '',
                venue_complement: null,
                venue_neighborhood: '',
                venue_city: campaign.venue_partner.venue_city,
                venue_state: campaign.venue_partner.venue_state,
                venue_photos: campaign.venue_partner.venue_photos || [],
                screen_quantity: campaign.venue_partner.screen_quantity,
                daily_footfall: null,
                average_retention: null,
                blacklisted_categories: [],
                custom_blacklist_brands: [],
                status: 'active',
                created_at: ''
            });
        }

        // Mudar para tab de criação no step de configuração
        setActiveTab('create');
        setStep(4); // Step 4 é a configuração para Venue

        // Expandir seções
        setIsPeriodExpanded(true);
        setIsDescExpanded(campaign.campaign_description ? true : false);
    };

    // Salvar edição de campanha Venue
    const handleSaveVenueCampaignEdit = async () => {
        if (!editingVenueCampaign || !packName.trim() || !startDate || !endDate) return;

        setIsSubmitting(true);

        try {
            console.log('💾 [WatchAdsModal] Salvando edição da campanha Venue:', editingVenueCampaign.id);

            const { error } = await supabase
                .from('venue_campaigns')
                .update({
                    campaign_name: packName,
                    campaign_description: packDescription || null,
                    start_date: startDate.toISOString().split('T')[0],
                    end_date: endDate.toISOString().split('T')[0],
                    media_ids: selectedMediaIds,
                    target_mobile: targetMobile,
                    target_fixed: targetFixed,
                    updated_at: new Date().toISOString()
                })
                .eq('id', editingVenueCampaign.id);

            if (error) {
                console.error('❌ [WatchAdsModal] Erro ao atualizar campanha Venue:', error);
                toast({
                    title: 'Erro ao atualizar campanha',
                    description: error.message,
                    variant: 'destructive',
                });
            } else {
                console.log('✅ [WatchAdsModal] Campanha Venue atualizada com sucesso!');

                // Atualizar lista local
                setVenueCampaigns(prev => prev.map(c =>
                    c.id === editingVenueCampaign.id
                        ? {
                            ...c,
                            campaign_name: packName,
                            campaign_description: packDescription,
                            start_date: startDate.toISOString().split('T')[0],
                            end_date: endDate.toISOString().split('T')[0],
                            media_ids: selectedMediaIds,
                            target_mobile: targetMobile,
                            target_fixed: targetFixed,
                            updated_at: new Date().toISOString()
                        }
                        : c
                ));

                toast({
                    title: 'Campanha atualizada!',
                    description: `"${packName}" foi salva com sucesso.`,
                });

                // Limpar estado de edição e voltar para lista
                setEditingVenueCampaign(null);
                setActiveTab('campaigns');

                // Resetar formulário
                setPackName('');
                setPackDescription('');
                setSelectedMediaIds([]);
                setSelectedCities([]);
                setStartDate(new Date());
                setEndDate(addDays(new Date(), 7));
                setTargetMobile(true);
                setTargetFixed(false);
                setAdsServiceType(null);
                setHasChosenService(false);
                setSelectedPartner(null);
                setStep(1);
            }
        } catch (err) {
            console.error('❌ [WatchAdsModal] Erro inesperado ao atualizar campanha Venue:', err);
            toast({
                title: 'Erro inesperado',
                description: 'Ocorreu um erro ao atualizar a campanha.',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Cancelar edição de campanha Venue
    const handleCancelVenueCampaignEdit = () => {
        setEditingVenueCampaign(null);
        setActiveTab('campaigns');

        // Resetar formulário
        setPackName('');
        setPackDescription('');
        setSelectedMediaIds([]);
        setSelectedCities([]);
        setStartDate(new Date());
        setEndDate(addDays(new Date(), 7));
        setTargetMobile(true);
        setTargetFixed(false);
        setAdsServiceType(null);
        setHasChosenService(false);
        setSelectedPartner(null);
        setStep(1);
    };

    // Editar campanha Urban
    const handleEditUrbanCampaign = (campaign: UrbanCampaign) => {
        // Bloquear edição se status for active
        if (campaign.status === 'active') {
            toast({
                title: 'Ação não permitida',
                description: 'Campanhas em veiculação não podem ser editadas.',
                variant: 'destructive',
            });
            return;
        }

        console.log('✏️ [WatchAdsModal] Editando campanha Urban:', campaign);
        setEditingUrbanCampaign(campaign);
        setPackName(campaign.campaign_name);
        setPackDescription(campaign.campaign_description || '');
        setSelectedMediaIds(campaign.media_ids);
        // Adicionar T00:00:00 para garantir que seja interpretado como hora local e não UTC
        setStartDate(new Date(campaign.start_date + 'T00:00:00'));
        setEndDate(new Date(campaign.end_date + 'T00:00:00'));
        setTargetMobile(campaign.target_mobile);
        setTargetFixed(campaign.target_fixed);
        setAdsServiceType('urban');
        setHasChosenService(true);
        setSelectedCities(campaign.circulation_cities || []);

        // Mudar para tab de criação no step de configuração
        setActiveTab('create');
        setStep(3); // Step 3 é a configuração para Urban

        // Expandir seções
        setIsPeriodExpanded(true);
        setIsCitiesExpanded(true);
        setIsDescExpanded(campaign.campaign_description ? true : false);
    };

    // Salvar edição de campanha Urban
    const handleSaveUrbanCampaignEdit = async () => {
        if (!editingUrbanCampaign || !packName.trim() || !startDate || !endDate || selectedCities.length === 0) return;

        setIsSubmitting(true);

        try {
            console.log('💾 [WatchAdsModal] Salvando edição da campanha Urban:', editingUrbanCampaign.id);

            const { error } = await supabase
                .from('urban_campaigns')
                .update({
                    campaign_name: packName,
                    campaign_description: packDescription || null,
                    start_date: startDate.toISOString().split('T')[0],
                    end_date: endDate.toISOString().split('T')[0],
                    media_ids: selectedMediaIds,
                    circulation_cities: selectedCities,
                    target_mobile: targetMobile,
                    target_fixed: targetFixed,
                    updated_at: new Date().toISOString()
                })
                .eq('id', editingUrbanCampaign.id);

            if (error) {
                console.error('❌ [WatchAdsModal] Erro ao atualizar campanha Urban:', error);
                toast({
                    title: 'Erro ao atualizar campanha',
                    description: error.message,
                    variant: 'destructive',
                });
            } else {
                console.log('✅ [WatchAdsModal] Campanha Urban atualizada com sucesso!');

                // Atualizar lista local
                setUrbanCampaigns(prev => prev.map(c =>
                    c.id === editingUrbanCampaign.id
                        ? {
                            ...c,
                            campaign_name: packName,
                            campaign_description: packDescription,
                            start_date: startDate.toISOString().split('T')[0],
                            end_date: endDate.toISOString().split('T')[0],
                            media_ids: selectedMediaIds,
                            circulation_cities: selectedCities,
                            target_mobile: targetMobile,
                            target_fixed: targetFixed,
                            updated_at: new Date().toISOString()
                        }
                        : c
                ));

                toast({
                    title: 'Campanha atualizada!',
                    description: `"${packName}" foi salva com sucesso.`,
                });

                // Limpar estado de edição e voltar para lista
                setEditingUrbanCampaign(null);
                setActiveTab('campaigns');
                setAdsServiceType('urban'); // Manter na view Urban

                // Resetar formulário
                setPackName('');
                setPackDescription('');
                setSelectedMediaIds([]);
                setSelectedCities([]);
                setStartDate(new Date());
                setEndDate(addDays(new Date(), 7));
                setTargetMobile(true);
                setTargetFixed(false);
                setHasChosenService(false);
                setStep(1);
            }
        } catch (err) {
            console.error('❌ [WatchAdsModal] Erro inesperado ao atualizar campanha Urban:', err);
            toast({
                title: 'Erro inesperado',
                description: 'Ocorreu um erro ao atualizar a campanha.',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Cancelar edição de campanha Urban
    const handleCancelUrbanCampaignEdit = () => {
        setEditingUrbanCampaign(null);
        setActiveTab('campaigns');
        setAdsServiceType('urban');

        // Resetar formulário
        setPackName('');
        setPackDescription('');
        setSelectedMediaIds([]);
        setSelectedCities([]);
        setStartDate(new Date());
        setEndDate(addDays(new Date(), 7));
        setTargetMobile(true);
        setTargetFixed(false);
        setHasChosenService(false);
        setStep(1);
    };

    // Marcar campanha Urban para exclusão
    // Marcar campanha Urban para exclusão
    const handleDeleteUrbanCampaign = (campaign: UrbanCampaign) => {
        setUrbanCampaignToDelete(campaign);
    };

    // Confirmar exclusão de campanha Urban
    const confirmDeleteUrbanCampaign = async () => {
        if (!urbanCampaignToDelete) return;

        try {
            console.log('🗑️ [WatchAdsModal] Excluindo campanha Urban:', urbanCampaignToDelete.id);

            const { error } = await supabase
                .from('urban_campaigns')
                .delete()
                .eq('id', urbanCampaignToDelete.id);

            if (error) {
                console.error('❌ [WatchAdsModal] Erro ao excluir campanha Urban:', error);
                toast({
                    title: 'Erro ao excluir campanha',
                    description: error.message,
                    variant: 'destructive',
                });
            } else {
                console.log('✅ [WatchAdsModal] Campanha Urban excluída com sucesso!');

                // Atualizar lista local
                setUrbanCampaigns(prev => prev.filter(c => c.id !== urbanCampaignToDelete.id));

                toast({
                    title: 'Campanha excluída',
                    description: `"${urbanCampaignToDelete.campaign_name}" foi removida com sucesso.`,
                });
            }
        } catch (err) {
            console.error('❌ [WatchAdsModal] Erro inesperado ao excluir campanha Urban:', err);
            toast({
                title: 'Erro inesperado',
                description: 'Ocorreu um erro ao excluir a campanha.',
                variant: 'destructive',
            });
        } finally {
            setUrbanCampaignToDelete(null);
        }
    };

    // Cancelar exclusão de campanha Urban
    const cancelDeleteUrbanCampaign = () => {
        setUrbanCampaignToDelete(null);
    };

    // Marcar campanha Venue para exclusão
    const handleDeleteVenueCampaign = (campaign: VenueCampaign) => {
        setVenueCampaignToDelete(campaign);
    };

    // Confirmar exclusão de campanha Venue
    const confirmDeleteVenueCampaign = async () => {
        if (!venueCampaignToDelete) return;

        try {
            console.log('🗑️ [WatchAdsModal] Excluindo campanha Venue:', venueCampaignToDelete.id);

            const { error } = await supabase
                .from('venue_campaigns')
                .delete()
                .eq('id', venueCampaignToDelete.id);

            if (error) {
                console.error('❌ [WatchAdsModal] Erro ao excluir campanha Venue:', error);
                toast({
                    title: 'Erro ao excluir campanha',
                    description: error.message,
                    variant: 'destructive',
                });
            } else {
                console.log('✅ [WatchAdsModal] Campanha Venue excluída com sucesso!');

                // Atualizar lista local
                setVenueCampaigns(prev => prev.filter(c => c.id !== venueCampaignToDelete.id));

                toast({
                    title: 'Campanha excluída',
                    description: `"${venueCampaignToDelete.campaign_name}" foi removida com sucesso.`,
                });
            }
        } catch (err) {
            console.error('❌ [WatchAdsModal] Erro inesperado ao excluir campanha Venue:', err);
            toast({
                title: 'Erro inesperado',
                description: 'Ocorreu um erro ao excluir a campanha.',
                variant: 'destructive',
            });
        } finally {
            setVenueCampaignToDelete(null);
        }
    };

    // Cancelar exclusão de campanha Venue
    const cancelDeleteVenueCampaign = () => {
        setVenueCampaignToDelete(null);
    };

    return (
        <>
            <Dialog open={open} onOpenChange={() => onClose()}>
                <DialogContent className="max-w-3xl max-h-[80vh] p-0 overflow-hidden flex flex-col !top-[55%]">
                    {/* Header compacto com gradiente - muda baseado no serviço selecionado */}
                    <div className={`p-4 text-white transition-all duration-300 flex-shrink-0 ${adsServiceType === 'venue'
                        ? 'bg-gradient-to-r from-green-600 via-emerald-500 to-orange-500'
                        : adsServiceType === 'urban'
                            ? 'bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-600'
                            : 'bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600'
                        }`}>
                        <div className="flex items-center gap-3">
                            {/* Ícone e título */}
                            <div className={`w-9 h-9 rounded-lg backdrop-blur-sm flex items-center justify-center ${adsServiceType === 'venue'
                                ? 'bg-gradient-to-br from-orange-400 to-amber-500'
                                : adsServiceType === 'urban'
                                    ? 'bg-gradient-to-br from-purple-400 to-pink-500'
                                    : 'bg-white/20'
                                }`}>
                                {adsServiceType === 'venue' ? (
                                    <Store className="h-5 w-5 text-white" />
                                ) : adsServiceType === 'urban' ? (
                                    <Car className="h-5 w-5 text-white" />
                                ) : (
                                    <Tv className="h-5 w-5 text-white" />
                                )}
                            </div>
                            <div className="flex-1">
                                <DialogTitle className="text-lg font-bold text-white">
                                    {editingCampaign || editingUrbanCampaign ? 'Editar Campanha' : (
                                        adsServiceType === 'venue' ? (
                                            <span className="flex items-center gap-2">
                                                <span className="italic text-orange-200" style={{ textShadow: '0 0 10px rgba(251, 146, 60, 0.6)' }}>ADS</span>
                                                <span>Venue</span>
                                            </span>
                                        ) : adsServiceType === 'urban' ? (
                                            <span className="flex items-center gap-2">
                                                <span className="italic text-purple-200" style={{ textShadow: '0 0 10px rgba(168, 85, 247, 0.6)' }}>ADS</span>
                                                <span>Urban</span>
                                            </span>
                                        ) : 'Watch Ads'
                                    )}
                                </DialogTitle>
                                <DialogDescription className={`text-xs mt-0 ${adsServiceType === 'venue' ? 'text-orange-100' :
                                    adsServiceType === 'urban' ? 'text-purple-100' : 'text-green-100'
                                    }`}>
                                    {editingCampaign || editingUrbanCampaign
                                        ? `Editando "${editingCampaign?.name || editingUrbanCampaign?.campaign_name}"`
                                        : activeTab === 'create'
                                            ? (step === 1
                                                ? 'Escolha o tipo de serviço ADS'
                                                : step === 2
                                                    ? (adsServiceType === 'urban' ? 'Selecione as mídias para a campanha' : 'Selecione um estabelecimento parceiro')
                                                    : 'Configure as regras de exibição')
                                            : 'Gerencie suas campanhas ativas'
                                    }
                                </DialogDescription>
                            </div>

                            {activeTab === 'create' && !editingCampaign && hasChosenService && (
                                <div className="flex items-center gap-1.5 mr-6">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? 'bg-white text-green-600' : step > 2 ? 'bg-white/60 text-green-600' : 'bg-white/30 text-white'
                                        }`}>
                                        1
                                    </div>
                                    <div className={`w-4 h-0.5 ${step > 2 ? 'bg-white/60' : 'bg-white/30'}`} />
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 3 ? 'bg-white text-green-600' : 'bg-white/30 text-white'
                                        }`}>
                                        2
                                    </div>
                                </div>
                            )}

                            {/* Ícone de edição quando estiver editando */}
                            {(editingCampaign || editingUrbanCampaign) && (
                                <div className="flex items-center gap-1.5 mr-6">
                                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                        <Pencil className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Tabs - apenas quando não estiver editando e já escolheu o serviço */}
                        {!editingCampaign && !editingUrbanCampaign && hasChosenService && (
                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={() => setActiveTab('create')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'create'
                                        ? 'bg-white text-green-700 shadow-md'
                                        : 'bg-white/20 text-white hover:bg-white/30'
                                        }`}
                                >
                                    <Plus className="h-4 w-4" />
                                    Criar Campanha
                                </button>
                                <button
                                    onClick={() => setActiveTab('campaigns')}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'campaigns'
                                        ? 'bg-white text-green-700 shadow-md'
                                        : 'bg-white/20 text-white hover:bg-white/30'
                                        }`}
                                >
                                    <List className="h-4 w-4" />
                                    Minhas Campanhas

                                </button>
                            </div>
                        )}
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 p-4 overflow-y-auto scrollbar-hide">
                        {/* TAB: Criar Campanha */}
                        {activeTab === 'create' && step === 1 && (
                            /* STEP 1: Seleção do Serviço ADS (primeiro passo ao abrir o modal) */
                            <div className="space-y-4 py-2 px-2">
                                <div className="text-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
                                        Escolha o tipo de veiculação
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Selecione onde sua campanha será exibida
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* ADS Urban */}
                                    <button
                                        type="button"
                                        onClick={() => adsUrbanEnabled && setAdsServiceType('urban')}
                                        disabled={!adsUrbanEnabled}
                                        className={`relative group p-5 rounded-2xl border-2 transition-all duration-300 overflow-hidden ${!adsUrbanEnabled
                                            ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                                            : adsServiceType === 'urban'
                                                ? 'border-emerald-500 ring-2 ring-emerald-500 ring-offset-2 bg-gradient-to-br from-purple-600 via-pink-500 to-fuchsia-600 shadow-[0_0_30px_rgba(168,85,247,0.5)]'
                                                : 'border-gray-200 bg-white hover:border-emerald-400 hover:ring-2 hover:ring-emerald-400 hover:ring-offset-1 hover:bg-gradient-to-br hover:from-purple-600 hover:via-pink-500 hover:to-fuchsia-600 hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]'
                                            }`}
                                    >
                                        {/* Badge de desativado */}
                                        {!adsUrbanEnabled && (
                                            <div className="absolute top-2 right-2 bg-gray-400 text-white text-[10px] font-medium px-2 py-0.5 rounded-full z-20">
                                                Indisponível
                                            </div>
                                        )}
                                        {/* Glow effect on hover/selected */}
                                        <div className={`absolute inset-0 transition-opacity duration-300 ${adsServiceType === 'urban' && adsUrbanEnabled ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
                                            }`}>
                                            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/30 via-pink-500/30 to-fuchsia-500/30 blur-xl" />
                                        </div>

                                        {/* Content */}
                                        <div className="relative z-10 text-left">
                                            {/* Check indicator */}
                                            <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${adsServiceType === 'urban'
                                                ? 'bg-white border-transparent shadow-[0_0_10px_rgba(255,255,255,0.6)]'
                                                : 'border-gray-300 bg-white group-hover:border-purple-300 group-hover:bg-purple-100'
                                                }`}>
                                                {adsServiceType === 'urban' && (
                                                    <CheckCircle2 className="h-4 w-4 text-purple-600" />
                                                )}
                                            </div>

                                            {/* Title */}
                                            <h4 className="text-2xl font-bold mb-2">
                                                <span className={`italic transition-all duration-300 ${adsServiceType === 'urban' ? 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 bg-clip-text text-transparent group-hover:text-white group-hover:bg-none group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]'}`}>
                                                    ADS
                                                </span>
                                                <span className={`ml-1.5 transition-all duration-300 ${adsServiceType === 'urban' ? 'text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]' : 'text-emerald-500 group-hover:text-white group-hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]'}`}>
                                                    Urban
                                                </span>
                                            </h4>

                                            {/* Slogan */}
                                            <p className={`text-sm italic transition-colors duration-300 ${adsServiceType === 'urban' ? 'text-white/90' : 'text-gray-500 group-hover:text-white/90'}`}>
                                                "Brands in urban motion."
                                            </p>

                                            {/* Explanation */}
                                            <p className={`text-xs mt-3 leading-relaxed transition-colors duration-300 ${adsServiceType === 'urban' ? 'text-white/80' : 'text-gray-400 group-hover:text-white/80'}`}>
                                                Sua campanha será exibida em telas digitais instaladas em veículos de aplicativos de mobilidade urbana, alcançando passageiros durante seus trajetos pela cidade.
                                            </p>

                                            {/* Icon indicator */}
                                            <div className={`mt-4 flex items-center gap-2 transition-colors duration-300 ${adsServiceType === 'urban' ? 'text-white' : 'text-purple-500 group-hover:text-white'}`}>
                                                <Car className="h-5 w-5" />
                                                <span className={`text-xs transition-colors duration-300 ${adsServiceType === 'urban' ? 'text-white/80' : 'text-gray-500 group-hover:text-white/80'}`}>Veículos de mobilidade urbana</span>
                                            </div>
                                        </div>
                                    </button>

                                    {/* ADS Venue */}
                                    <button
                                        type="button"
                                        onClick={() => adsVenueEnabled && setAdsServiceType('venue')}
                                        disabled={!adsVenueEnabled}
                                        className={`relative group p-5 rounded-2xl border-2 transition-all duration-300 overflow-hidden ${!adsVenueEnabled
                                                ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                                                : adsServiceType === 'venue'
                                                    ? 'border-emerald-500 ring-2 ring-emerald-500 ring-offset-2 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 shadow-[0_0_30px_rgba(251,146,60,0.5)]'
                                                    : 'border-gray-200 bg-white hover:border-emerald-400 hover:ring-2 hover:ring-emerald-400 hover:ring-offset-1 hover:bg-gradient-to-br hover:from-orange-500 hover:via-amber-500 hover:to-yellow-500 hover:shadow-[0_0_20px_rgba(251,146,60,0.4)]'
                                            }`}
                                    >
                                        {/* Badge de desativado */}
                                        {!adsVenueEnabled && (
                                            <div className="absolute top-2 right-2 bg-gray-400 text-white text-[10px] font-medium px-2 py-0.5 rounded-full z-20">
                                                Indisponível
                                            </div>
                                        )}
                                        {/* Glow effect on hover/selected */}
                                        <div className={`absolute inset-0 transition-opacity duration-300 ${adsServiceType === 'venue' && adsVenueEnabled ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
                                            }`}>
                                            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 via-amber-500/30 to-yellow-500/30 blur-xl" />
                                        </div>

                                        {/* Content */}
                                        <div className="relative z-10 text-left">
                                            {/* Check indicator */}
                                            <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${!adsVenueEnabled
                                                    ? 'border-gray-300 bg-gray-200'
                                                    : adsServiceType === 'venue'
                                                        ? 'bg-white border-transparent shadow-[0_0_10px_rgba(255,255,255,0.6)]'
                                                        : 'border-gray-300 bg-white group-hover:border-orange-300 group-hover:bg-orange-100'
                                                }`}>
                                                {adsServiceType === 'venue' && adsVenueEnabled && (
                                                    <CheckCircle2 className="h-4 w-4 text-orange-600" />
                                                )}
                                            </div>

                                            {/* Title */}
                                            <h4 className="text-2xl font-bold mb-2">
                                                <span className={`italic transition-all duration-300 ${adsServiceType === 'venue' ? 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 bg-clip-text text-transparent group-hover:text-white group-hover:bg-none group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]'}`}>
                                                    ADS
                                                </span>
                                                <span className={`ml-1.5 transition-all duration-300 ${adsServiceType === 'venue' ? 'text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]' : 'text-emerald-500 group-hover:text-white group-hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]'}`}>
                                                    Venue
                                                </span>
                                            </h4>

                                            {/* Slogan */}
                                            <p className={`text-sm italic transition-colors duration-300 ${adsServiceType === 'venue' ? 'text-white/90' : 'text-gray-500 group-hover:text-white/90'}`}>
                                                "Indoor spaces & experiences."
                                            </p>

                                            {/* Explanation */}
                                            <p className={`text-xs mt-3 leading-relaxed transition-colors duration-300 ${adsServiceType === 'venue' ? 'text-white/80' : 'text-gray-400 group-hover:text-white/80'}`}>
                                                Sua campanha será exibida em telas digitais instaladas em estabelecimentos parceiros, alcançando clientes em espaços como restaurantes, academias e comércios.
                                            </p>

                                            {/* Icon indicator */}
                                            <div className={`mt-4 flex items-center gap-2 transition-colors duration-300 ${adsServiceType === 'venue' ? 'text-white' : 'text-orange-500 group-hover:text-white'}`}>
                                                <Building2 className="h-5 w-5" />
                                                <span className={`text-xs transition-colors duration-300 ${adsServiceType === 'venue' ? 'text-white/80' : 'text-gray-500 group-hover:text-white/80'}`}>Telas em locais parceiros fixos</span>
                                            </div>
                                        </div>
                                    </button>
                                </div>

                                {/* Info text */}
                                <div className="text-center text-xs text-gray-500 mt-2">
                                    <Info className="h-4 w-4 inline-block mr-1 mb-0.5" />
                                    Selecione uma opção para continuar
                                </div>
                            </div>
                        )}

                        {activeTab === 'create' && step === 2 && (
                            /* STEP 2: Seleção de Mídias (tanto Urban quanto Venue) */
                            <div className="space-y-4">
                                {/* Header com busca */}
                                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                                    <div className="relative flex-1 w-full sm:max-w-sm">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            placeholder="Buscar mídias..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-9 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                        />
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1.5">
                                            <CheckCircle2 className="h-4 w-4 mr-1.5" />
                                            {selectedMediaIds.length} selecionada{selectedMediaIds.length !== 1 ? 's' : ''}
                                        </Badge>


                                    </div>
                                </div>

                                {/* Grid de Mídias */}
                                <ScrollArea className="h-[280px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
                                    {filteredMedias.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                            <Image className="h-12 w-12 mb-3 opacity-50" />
                                            <p className="text-sm">
                                                {searchQuery ? 'Nenhuma mídia encontrada' : 'Você ainda não possui mídias'}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Crie mídias no dashboard para poder adicioná-las a um pack
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                            {filteredMedias.map((media) => {
                                                const isSelected = selectedMediaIds.includes(media.id);

                                                return (
                                                    <Card
                                                        key={media.id}
                                                        onClick={() => handleToggleMedia(media.id)}
                                                        className={`group cursor-pointer overflow-hidden transition-all duration-200 hover:shadow-lg ${isSelected
                                                            ? 'ring-2 ring-green-500 ring-offset-2 shadow-md'
                                                            : 'hover:ring-2 hover:ring-gray-300'
                                                            }`}
                                                    >
                                                        <div className="relative aspect-video bg-gray-100 dark:bg-gray-800">
                                                            <img
                                                                src={getThumbnailUrl(media)}
                                                                alt={media.title}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/320x180/10b981/ffffff?text=Mídia';
                                                                }}
                                                            />

                                                            <div className={`absolute inset-0 transition-all duration-200 ${isSelected ? 'bg-green-500/20' : 'bg-black/0 group-hover:bg-black/10'
                                                                }`} />

                                                            <div className={`absolute top-2 right-2 transition-all duration-200 ${isSelected || 'opacity-0 group-hover:opacity-100'
                                                                }`}>
                                                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isSelected
                                                                    ? 'bg-green-500 text-white'
                                                                    : 'bg-white/90 text-gray-400 shadow-sm'
                                                                    }`}>
                                                                    {isSelected && <CheckCircle2 className="h-3 w-3" />}
                                                                </div>
                                                            </div>

                                                            {/* Mostrar ícone do tipo de mídia (sem tempo local) */}
                                                            <div className="absolute bottom-1.5 left-1.5">
                                                                <div className="flex items-center gap-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full px-1.5 py-0.5 text-xs shadow-sm">
                                                                    {getMediaTypeIcon(media.type, media.url)}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <CardContent className="p-2">
                                                            <h4 className="font-medium text-xs text-gray-900 dark:text-white truncate">
                                                                {media.title}
                                                            </h4>
                                                        </CardContent>
                                                    </Card>
                                                );
                                            })}
                                        </div>
                                    )}
                                </ScrollArea>
                            </div>
                        )}

                        {activeTab === 'create' && step === 3 && adsServiceType === 'venue' && (
                            /* STEP 3: Lista de Parceiros (ADS Venue) - após seleção de mídias */
                            <div className="space-y-4">
                                {/* Header com título e busca */}
                                <div className="flex flex-col gap-3">
                                    <div className="text-center">
                                        <h3 className="text-base font-semibold text-gray-800 dark:text-white mb-1">
                                            Estabelecimentos Parceiros
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Selecione um parceiro para ver detalhes e disponibilidade
                                        </p>
                                    </div>

                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            placeholder="Buscar por nome, cidade ou tipo..."
                                            value={partnerSearchQuery}
                                            onChange={(e) => setPartnerSearchQuery(e.target.value)}
                                            className="pl-9 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                        />
                                    </div>

                                    {/* Filtros de Região */}
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            onClick={() => setSelectedCityFilter(null)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${selectedCityFilter === null
                                                ? 'bg-orange-500 text-white shadow-md'
                                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-orange-300'
                                                }`}
                                        >
                                            <MapPin className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                                            Todas as regiões
                                        </button>
                                        <button
                                            onClick={() => setSelectedCityFilter(selectedCityFilter === 'florianopolis' ? null : 'florianopolis')}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${selectedCityFilter === 'florianopolis'
                                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-emerald-300'
                                                }`}
                                        >
                                            <Building2 className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                                            Florianópolis e Região
                                        </button>
                                        <button
                                            onClick={() => setSelectedCityFilter(selectedCityFilter === 'litoral' ? null : 'litoral')}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${selectedCityFilter === 'litoral'
                                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                                                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-blue-300'
                                                }`}
                                        >
                                            <Sparkles className="h-3 w-3 inline-block mr-1 -mt-0.5" />
                                            Itapema e Balneário
                                        </button>
                                    </div>
                                </div>

                                {/* Lista de Parceiros */}
                                <ScrollArea className="h-[280px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                                    {loadingPartners ? (
                                        <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                                            <Loader2 className="h-8 w-8 animate-spin text-orange-500 mb-3" />
                                            <p className="text-sm">Carregando parceiros...</p>
                                        </div>
                                    ) : filteredPartners.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                                            <Store className="h-12 w-12 mb-3 opacity-50" />
                                            <p className="text-sm text-center px-4">
                                                {partnerSearchQuery || selectedCityFilter
                                                    ? 'Nenhum parceiro encontrado para os filtros selecionados'
                                                    : 'Nenhum parceiro disponível'}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1 text-center px-4">
                                                {partnerSearchQuery || selectedCityFilter
                                                    ? 'Tente ajustar a busca ou remover os filtros'
                                                    : 'Em breve novos estabelecimentos estarão disponíveis'}
                                            </p>
                                            {(partnerSearchQuery || selectedCityFilter) && (
                                                <button
                                                    onClick={() => {
                                                        setPartnerSearchQuery('');
                                                        setSelectedCityFilter(null);
                                                    }}
                                                    className="mt-3 text-xs text-orange-500 hover:text-orange-600 font-medium"
                                                >
                                                    Limpar filtros
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                            {filteredPartners.map((partner) => {
                                                const isExpanded = selectedPartner?.id === partner.id;
                                                const venueTypeLabel = VENUE_TYPE_LABELS[partner.venue_type] || partner.venue_type;
                                                const logoUrl = partner.venue_photos?.[0] || 'https://via.placeholder.com/80x80/f97316/ffffff?text=' + partner.venue_name.charAt(0).toUpperCase();

                                                return (
                                                    <div key={partner.id} className="bg-white dark:bg-gray-800">
                                                        {/* Item da lista */}
                                                        <div
                                                            onClick={() => setSelectedPartner(isExpanded ? null : partner)}
                                                            className={`flex items-center gap-3 p-3 cursor-pointer transition-all duration-200 hover:bg-orange-50 dark:hover:bg-orange-900/20 ${isExpanded ? 'bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500' : ''
                                                                }`}
                                                        >
                                                            {/* Logo */}
                                                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30">
                                                                <img
                                                                    src={logoUrl}
                                                                    alt={partner.venue_name}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        (e.target as HTMLImageElement).src = `https://via.placeholder.com/80x80/f97316/ffffff?text=${partner.venue_name.charAt(0).toUpperCase()}`;
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Informações básicas */}
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                                                    {partner.venue_name}
                                                                </h4>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0 border-orange-200">
                                                                        {venueTypeLabel}
                                                                    </Badge>
                                                                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                                                        <MapPin className="h-3 w-3" />
                                                                        {partner.venue_city}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Indicadores rápidos */}
                                                            <div className="flex items-center gap-3 mr-2">
                                                                <div className="text-center">
                                                                    <div className="flex items-center gap-1 text-orange-600">
                                                                        <Tv className="h-3.5 w-3.5" />
                                                                        <span className="font-bold text-sm">{partner.active_device_count !== undefined ? partner.active_device_count : 0}</span>
                                                                    </div>
                                                                    <span className="text-[9px] text-gray-400">telas</span>
                                                                </div>
                                                                {partner.daily_footfall && (
                                                                    <div className="text-center">
                                                                        <div className="flex items-center gap-1 text-blue-600">
                                                                            <Users className="h-3.5 w-3.5" />
                                                                            <span className="font-bold text-sm">{partner.daily_footfall >= 1000 ? `${(partner.daily_footfall / 1000).toFixed(1)}k` : partner.daily_footfall}</span>
                                                                        </div>
                                                                        <span className="text-[9px] text-gray-400">pessoas/dia</span>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Chevron */}
                                                            {isExpanded ? (
                                                                <ChevronUp className="h-5 w-5 text-orange-500" />
                                                            ) : (
                                                                <ChevronDown className="h-5 w-5 text-gray-400" />
                                                            )}
                                                        </div>

                                                        {/* Detalhes expandidos */}
                                                        <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-[400px]' : 'max-h-0'}`}>
                                                            <div className="px-4 pb-4 pt-2 bg-gradient-to-b from-orange-50 to-white dark:from-orange-900/10 dark:to-gray-800">
                                                                {/* Grid de informações */}
                                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                                                    <div className="bg-white dark:bg-gray-700 rounded-lg p-2.5 shadow-sm border border-orange-100 dark:border-orange-900/30">
                                                                        <div className="flex items-center gap-1.5 text-orange-600 mb-1">
                                                                            <Tv className="h-4 w-4" />
                                                                            <span className="text-[10px] uppercase font-medium">Telas</span>
                                                                        </div>
                                                                        <p className="text-lg font-bold text-gray-900 dark:text-white">{partner.active_device_count !== undefined ? partner.active_device_count : 0}</p>
                                                                    </div>

                                                                    <div className="bg-white dark:bg-gray-700 rounded-lg p-2.5 shadow-sm border border-blue-100 dark:border-blue-900/30">
                                                                        <div className="flex items-center gap-1.5 text-blue-600 mb-1">
                                                                            <Users className="h-4 w-4" />
                                                                            <span className="text-[10px] uppercase font-medium">Frequência</span>
                                                                        </div>
                                                                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                                            {partner.daily_footfall ? `${partner.daily_footfall.toLocaleString('pt-BR')}/dia` : '-'}
                                                                        </p>
                                                                    </div>

                                                                    <div className="bg-white dark:bg-gray-700 rounded-lg p-2.5 shadow-sm border border-purple-100 dark:border-purple-900/30">
                                                                        <div className="flex items-center gap-1.5 text-purple-600 mb-1">
                                                                            <Timer className="h-4 w-4" />
                                                                            <span className="text-[10px] uppercase font-medium">Permanência</span>
                                                                        </div>
                                                                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                                            {partner.average_retention ? `${partner.average_retention}min` : '-'}
                                                                        </p>
                                                                    </div>

                                                                    <div className="bg-white dark:bg-gray-700 rounded-lg p-2.5 shadow-sm border border-green-100 dark:border-green-900/30">
                                                                        <div className="flex items-center gap-1.5 text-green-600 mb-1">
                                                                            <Eye className="h-4 w-4" />
                                                                            <span className="text-[10px] uppercase font-medium">Impactos Est.</span>
                                                                        </div>
                                                                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                                            {partner.daily_footfall && (partner.active_device_count !== undefined ? partner.active_device_count : partner.screen_quantity)
                                                                                ? `${((partner.daily_footfall * (partner.active_device_count !== undefined ? partner.active_device_count : partner.screen_quantity) * 0.7) / 1000).toFixed(1)}k`
                                                                                : '-'}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                {/* Endereço */}
                                                                <div className="bg-white dark:bg-gray-700 rounded-lg p-3 shadow-sm border border-gray-100 dark:border-gray-600 mb-3">
                                                                    <div className="flex items-start gap-2">
                                                                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                                        <div>
                                                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                                {partner.venue_street}, {partner.venue_number}
                                                                                {partner.venue_complement && ` - ${partner.venue_complement}`}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">
                                                                                {partner.venue_neighborhood} - {partner.venue_city}/{partner.venue_state}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Fotos do estabelecimento */}
                                                                {partner.venue_photos && partner.venue_photos.length > 0 && (
                                                                    <div>
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <p className="text-[10px] uppercase font-medium text-gray-500">Fotos do Estabelecimento</p>
                                                                            <span className="text-[9px] text-orange-500 flex items-center gap-0.5">
                                                                                <ZoomIn className="h-3 w-3" />
                                                                                Clique para ampliar
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex gap-2 overflow-x-auto pb-2">
                                                                            {partner.venue_photos.slice(0, 4).map((photo, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 relative group cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-orange-400 hover:shadow-lg"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        openPhotoCarousel(partner.venue_photos, partner.venue_name, index);
                                                                                    }}
                                                                                >
                                                                                    <img
                                                                                        src={photo}
                                                                                        alt={`${partner.venue_name} - Foto ${index + 1}`}
                                                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                                                                    />
                                                                                    {/* Overlay com ícone de zoom */}
                                                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center">
                                                                                        <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow-lg" />
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                            {partner.venue_photos.length > 4 && (
                                                                                <div
                                                                                    className="w-20 h-14 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 flex items-center justify-center flex-shrink-0 cursor-pointer hover:from-orange-200 hover:to-amber-200 dark:hover:from-orange-900/50 dark:hover:to-amber-900/50 transition-all duration-200"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        openPhotoCarousel(partner.venue_photos, partner.venue_name, 4);
                                                                                    }}
                                                                                >
                                                                                    <span className="text-xs font-bold text-orange-600 dark:text-orange-400">
                                                                                        +{partner.venue_photos.length - 4}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Botão de ação */}
                                                                <Button
                                                                    className="w-full mt-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleSelectVenuePartner(partner);
                                                                    }}
                                                                    disabled={isCreatingVenueCampaign || selectedMediaIds.length === 0}
                                                                >
                                                                    {isCreatingVenueCampaign ? (
                                                                        <>
                                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                            Criando campanha...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                                                            Selecionar este parceiro
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </ScrollArea>
                            </div>
                        )}



                        {activeTab === 'create' && ((step === 3 && adsServiceType === 'urban') || (step === 4 && adsServiceType === 'venue')) && (
                            /* STEP 3 (Urban) ou STEP 4 (Venue): Configuração do Pack - Mobile First */
                            <div className="space-y-3 pb-4">
                                {/* Header para campanhas externas */}
                                {isExternalCampaign && (
                                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                                        <div className="flex items-center gap-2">
                                            <Store className="h-5 w-5 text-orange-600" />
                                            <div>
                                                <h3 className="font-semibold text-orange-800 dark:text-orange-300">Campanha Externa</h3>
                                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                                    Sua campanha será exibida nos aparelhos do parceiro selecionado após sua aprovação de investimento e por nossa equipe. Em breve entraremos em contato.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Nome da Campanha Ads - sempre visível */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                    <Label htmlFor="pack-name" className="text-sm font-medium flex items-center gap-1.5 mb-2">
                                        <Target className="h-3.5 w-3.5 text-green-600" />
                                        Nome da Campanha Ads *
                                    </Label>
                                    <Input
                                        id="pack-name"
                                        placeholder="Ex: Campanha Centro Floripa"
                                        value={packName}
                                        onChange={(e) => setPackName(e.target.value)}
                                        className="h-9 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-sm"
                                    />
                                </div>

                                {/* Descrição - Colapsável */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                    <div
                                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                        onClick={() => setIsDescExpanded(!isDescExpanded)}
                                    >
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Descrição (opcional)
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {packDescription && !isDescExpanded && (
                                                <span className="text-xs text-green-600 truncate max-w-[100px]">
                                                    {packDescription.slice(0, 20)}...
                                                </span>
                                            )}
                                            {isDescExpanded ? (
                                                <ChevronUp className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                    <div className={`transition-all duration-200 overflow-hidden ${isDescExpanded ? 'max-h-40' : 'max-h-0'}`}>
                                        <div className="px-3 pb-3">
                                            <Textarea
                                                placeholder="Descreva o objetivo desta campanha..."
                                                value={packDescription}
                                                onChange={(e) => setPackDescription(e.target.value)}
                                                className="min-h-[60px] bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 resize-none text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Card do Parceiro Selecionado (apenas para Venue) */}
                                {adsServiceType === 'venue' && selectedPartner && (
                                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-orange-200 dark:border-orange-800 p-3">
                                        <Label className="text-sm font-medium flex items-center gap-1.5 mb-2 text-orange-800 dark:text-orange-300">
                                            <Store className="h-3.5 w-3.5" />
                                            Parceiro Selecionado
                                        </Label>
                                        <div className="flex items-start gap-3">
                                            {/* Thumbnail do estabelecimento */}
                                            {selectedPartner.venue_photos && selectedPartner.venue_photos.length > 0 && (
                                                <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-orange-200">
                                                    <img
                                                        src={selectedPartner.venue_photos[0]}
                                                        alt={selectedPartner.venue_name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                                                    {selectedPartner.venue_name}
                                                </h4>
                                                <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                                    <MapPin className="h-3 w-3 text-orange-500" />
                                                    <span className="truncate">
                                                        {selectedPartner.venue_city}, {selectedPartner.venue_state}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 mt-1.5">
                                                    <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0 border-0">
                                                        {VENUE_TYPE_LABELS[selectedPartner.venue_type] || selectedPartner.venue_type}
                                                    </Badge>
                                                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                                        <Tv className="h-3 w-3" />
                                                        {(selectedPartner.active_device_count !== undefined ? selectedPartner.active_device_count : selectedPartner.screen_quantity)} tela{(selectedPartner.active_device_count !== undefined ? selectedPartner.active_device_count : selectedPartner.screen_quantity) !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Cidades - Colapsável (apenas para Urban) */}
                                {adsServiceType === 'urban' && (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                        <div
                                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                            onClick={() => setIsCitiesExpanded(!isCitiesExpanded)}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <MapPin className="h-3.5 w-3.5 text-green-600" />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Cidades de Circulação * (Corridas auditadas)
                                                </span>
                                                {selectedCities.length > 0 && (
                                                    <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                                                        {selectedCities.length}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleSelectAllCities(); }}
                                                    className="text-[11px] font-medium text-green-600 hover:text-green-700"
                                                >
                                                    {selectedCities.length === AVAILABLE_CITIES.length ? 'Limpar' : 'Todas'}
                                                </button>
                                                {isCitiesExpanded ? (
                                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Preview quando colapsado */}
                                        {!isCitiesExpanded && selectedCities.length > 0 && (
                                            <div className="px-3 pb-2">
                                                <div className="flex flex-wrap gap-1">
                                                    {selectedCities.slice(0, 3).map(value => {
                                                        const city = AVAILABLE_CITIES.find(c => c.value === value);
                                                        return city ? (
                                                            <Badge key={value} className="bg-green-600 text-white text-[10px] py-0 px-1.5">
                                                                {city.label}
                                                            </Badge>
                                                        ) : null;
                                                    })}
                                                    {selectedCities.length > 3 && (
                                                        <Badge className="bg-gray-500 text-white text-[10px] py-0 px-1.5">
                                                            +{selectedCities.length - 3}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Área expandida */}
                                        <div className={`transition-all duration-200 overflow-hidden ${isCitiesExpanded ? 'max-h-60' : 'max-h-0'}`}>
                                            <div className="px-3 pb-3">
                                                <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg max-h-[120px] overflow-y-auto">
                                                    {AVAILABLE_CITIES.map((city) => {
                                                        const isSelected = selectedCities.includes(city.value);
                                                        return (
                                                            <Badge
                                                                key={city.value}
                                                                variant={isSelected ? 'default' : 'outline'}
                                                                className={`cursor-pointer transition-all text-xs py-0.5 px-2 ${isSelected
                                                                    ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                                                                    : 'bg-white dark:bg-gray-700 hover:bg-green-50 hover:border-green-500 text-gray-600'
                                                                    }`}
                                                                onClick={() => handleToggleCity(city.value)}
                                                            >
                                                                {city.label}
                                                                {isSelected && <X className="ml-1 h-2.5 w-2.5" />}
                                                            </Badge>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Período - Colapsável */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                    <div
                                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                        onClick={() => setIsPeriodExpanded(!isPeriodExpanded)}
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-green-600" />
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Período *
                                            </span>
                                            {duration > 0 && (
                                                <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0">
                                                    {duration}d
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!isPeriodExpanded && startDate && endDate && (
                                                <span className="text-xs text-gray-500">
                                                    {format(startDate, 'dd/MM')} - {format(endDate, 'dd/MM')}
                                                </span>
                                            )}
                                            {isPeriodExpanded ? (
                                                <ChevronUp className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>
                                    </div>

                                    <div className={`transition-all duration-200 overflow-hidden ${isPeriodExpanded ? 'max-h-60' : 'max-h-0'}`}>
                                        <div className="px-3 pb-3 space-y-2">
                                            {/* Atalhos rápidos */}
                                            <div className="flex gap-1.5">
                                                {[7, 14, 30].map((days) => (
                                                    <Badge
                                                        key={days}
                                                        variant="outline"
                                                        className="cursor-pointer hover:bg-green-50 hover:border-green-500 hover:text-green-700 text-xs flex-1 justify-center"
                                                        onClick={() => {
                                                            const start = startDate || new Date();
                                                            setStartDate(start);
                                                            setEndDate(addDays(start, days - 1));
                                                        }}
                                                    >
                                                        {days}d
                                                    </Badge>
                                                ))}
                                            </div>

                                            {/* Date pickers */}
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <Label className="text-[10px] text-gray-500 uppercase">Início</Label>
                                                    <Popover open={isStartOpen} onOpenChange={setIsStartOpen}>
                                                        <PopoverTrigger asChild>
                                                            <Button variant="outline" className="w-full justify-start h-8 text-xs bg-gray-50 dark:bg-gray-900">
                                                                <CalendarIcon className="mr-1.5 h-3 w-3 text-green-600" />
                                                                {startDate ? format(startDate, "dd/MM/yy", { locale: ptBR }) : 'Selecionar'}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="start">
                                                            <Calendar
                                                                mode="single"
                                                                selected={startDate}
                                                                onSelect={(d) => { setStartDate(d); setIsStartOpen(false); }}
                                                                disabled={(date) => date < new Date()}
                                                                locale={ptBR}
                                                                initialFocus
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>

                                                <div>
                                                    <Label className="text-[10px] text-gray-500 uppercase">Término</Label>
                                                    <Popover open={isEndOpen} onOpenChange={setIsEndOpen}>
                                                        <PopoverTrigger asChild>
                                                            <Button variant="outline" className="w-full justify-start h-8 text-xs bg-gray-50 dark:bg-gray-900" disabled={!startDate}>
                                                                <CalendarIcon className="mr-1.5 h-3 w-3 text-green-600" />
                                                                {endDate ? format(endDate, "dd/MM/yy", { locale: ptBR }) : 'Selecionar'}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="start">
                                                            <Calendar
                                                                mode="single"
                                                                selected={endDate}
                                                                onSelect={(d) => { setEndDate(d); setIsEndOpen(false); }}
                                                                disabled={(date) => startDate ? date < startDate : date < new Date()}
                                                                locale={ptBR}
                                                                initialFocus
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Resumo compacto - sempre visível */}
                                <div className={`rounded-lg p-3 border ${isExternalCampaign
                                    ? 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 border-orange-200 dark:border-orange-800'
                                    : 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-200 dark:border-green-800'
                                    }`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-1.5">
                                            <Sparkles className={`h-3.5 w-3.5 ${isExternalCampaign ? 'text-orange-600' : 'text-green-600'}`} />
                                            <span className={`text-xs font-semibold ${isExternalCampaign ? 'text-orange-800 dark:text-orange-300' : 'text-green-800 dark:text-green-300'}`}>Resumo</span>
                                        </div>
                                        {/* Mídias selecionadas - mini preview */}
                                        <div className="flex -space-x-1">
                                            {selectedMediaIds.slice(0, 3).map(id => {
                                                const media = medias.find(m => m.id === id);
                                                return media ? (
                                                    <div key={id} className="w-5 h-5 rounded border border-white shadow-sm overflow-hidden">
                                                        <img src={getThumbnailUrl(media)} alt="" className="w-full h-full object-cover" />
                                                    </div>
                                                ) : null;
                                            })}
                                            {selectedMediaIds.length > 3 && (
                                                <div className="w-5 h-5 rounded bg-gray-600 text-white flex items-center justify-center text-[8px] font-bold border border-white">
                                                    +{selectedMediaIds.length - 3}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Grid de resumo - Venue mostra apenas Mídias, Dias e Status */}
                                    <div className="grid gap-2 text-xs grid-cols-3">
                                        <div className="bg-white/60 dark:bg-gray-800/60 rounded p-1.5 text-center">
                                            <div className="text-gray-500 text-[10px]">Mídias</div>
                                            <div className={`font-bold ${isExternalCampaign ? 'text-orange-700' : 'text-green-700'}`}>{selectedMediaIds.length}</div>
                                        </div>

                                        <div className="bg-white/60 dark:bg-gray-800/60 rounded p-1.5 text-center">
                                            <div className="text-gray-500 text-[10px]">Dias</div>
                                            <div className={`font-bold ${isExternalCampaign ? 'text-orange-700' : 'text-green-700'}`}>{duration || '-'}</div>
                                        </div>

                                        <div className="bg-white/60 dark:bg-gray-800/60 rounded p-1.5 text-center">
                                            <div className="text-gray-500 text-[10px]">Status</div>
                                            <div className={`font-bold ${canSubmit
                                                ? (isExternalCampaign ? 'text-orange-700' : 'text-green-700')
                                                : 'text-orange-500'
                                                }`}>
                                                {canSubmit ? '✓' : '...'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB: Minhas Campanhas */}
                        {activeTab === 'campaigns' && (
                            <div className="space-y-3 pb-4">

                                {/* LISTA DE CAMPANHAS URBAN (Apenas se adsServiceType for null ou 'urban') */}
                                {adsServiceType === 'urban' && (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                        <div
                                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                            onClick={() => setIsCampaignsListExpanded(!isCampaignsListExpanded)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Car className="h-4 w-4 text-purple-600" />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Campanhas Urban
                                                </span>
                                                <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">
                                                    {loadingUrbanCampaigns ? '...' : urbanCampaigns.length}
                                                </Badge>
                                            </div>
                                            {isCampaignsListExpanded ? (
                                                <ChevronUp className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>

                                        <div className={`transition-all duration-300 overflow-hidden ${isCampaignsListExpanded ? 'max-h-none' : 'max-h-0'}`}>
                                            <div className="overflow-y-auto max-h-[350px] px-3 pb-4 space-y-2">
                                                {loadingUrbanCampaigns ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-purple-500" />
                                                        <p className="text-sm font-medium">Carregando campanhas...</p>
                                                    </div>
                                                ) : urbanCampaigns.length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <Car className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                                        <p className="text-sm font-medium">Nenhuma campanha Urban criada</p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Crie sua primeira campanha para anunciar em veículos
                                                        </p>
                                                    </div>
                                                ) : (
                                                    urbanCampaigns.map((campaign) => {
                                                        const isExpanded = expandedCampaignId === campaign.id;
                                                        const statusConfig = getStatusConfig(campaign.status);

                                                        return (
                                                            <div
                                                                key={campaign.id}
                                                                className={`border rounded-lg overflow-hidden transition-all ${isExpanded
                                                                    ? 'border-purple-300 bg-purple-50/50 dark:bg-purple-900/20'
                                                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                                                    }`}
                                                            >
                                                                {/* Header da Campanha */}
                                                                <div
                                                                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                                                    onClick={() => setExpandedCampaignId(isExpanded ? null : campaign.id)}
                                                                >
                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${campaign.status === 'active' ? 'bg-green-500' :
                                                                            campaign.status === 'pending' ? 'bg-yellow-500' :
                                                                                campaign.status === 'ended' ? 'bg-gray-400' : 'bg-gray-400'
                                                                            }`} />
                                                                        <div className="min-w-0 flex-1">
                                                                            <h4 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                                                                {campaign.campaign_name}
                                                                            </h4>
                                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                                <span className="text-[10px] text-gray-500">
                                                                                    {campaign.media_ids?.length || 0} mídia{(campaign.media_ids?.length || 0) !== 1 ? 's' : ''}
                                                                                </span>
                                                                                <span className="text-gray-300">•</span>
                                                                                <span className="text-[10px] text-purple-600 truncate max-w-[120px] flex items-center gap-1">
                                                                                    <MapPin className="h-3 w-3" />
                                                                                    {campaign.circulation_cities?.length || 0} cidade{(campaign.circulation_cities?.length || 0) !== 1 ? 's' : ''}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Badge className={`text-[10px] px-2 py-0.5 border ${statusConfig.color}`}>
                                                                            {statusConfig.label}
                                                                        </Badge>
                                                                        {isExpanded ? (
                                                                            <ChevronUp className="h-4 w-4 text-gray-400" />
                                                                        ) : (
                                                                            <ChevronDown className="h-4 w-4 text-gray-400" />
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Conteúdo expandido */}
                                                                <div className={`transition-all duration-200 overflow-hidden ${isExpanded ? 'max-h-none' : 'max-h-0'}`}>
                                                                    <div className="px-3 pb-3 pt-0 space-y-3">
                                                                        {/* Descrição */}
                                                                        {campaign.campaign_description && (
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                                    {campaign.campaign_description}
                                                                                </p>
                                                                            </div>
                                                                        )}

                                                                        {/* Cidades de circulação */}
                                                                        {campaign.circulation_cities && campaign.circulation_cities.length > 0 && (
                                                                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2.5">
                                                                                <div className="flex items-center gap-1.5 text-purple-600 mb-1.5">
                                                                                    <MapPin className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Cidades (Corridas auditadas)</span>
                                                                                </div>
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {campaign.circulation_cities.slice(0, 5).map(city => (
                                                                                        <Badge key={city} className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0 border-0">
                                                                                            {getCityLabel(city)}
                                                                                        </Badge>
                                                                                    ))}
                                                                                    {campaign.circulation_cities.length > 5 && (
                                                                                        <Badge className="bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0 border-0">
                                                                                            +{campaign.circulation_cities.length - 5}
                                                                                        </Badge>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* Mídias da Campanha (Preview) */}
                                                                        {campaign.media_ids && campaign.media_ids.length > 0 && (
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <div className="flex items-center gap-1.5 text-gray-500 mb-2">
                                                                                    <Image className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Mídias ({campaign.media_ids.length})</span>
                                                                                </div>
                                                                                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                                                                                    {campaign.media_ids.map(mediaId => {
                                                                                        const media = medias.find(m => m.id === mediaId);
                                                                                        if (!media) return null;
                                                                                        return (
                                                                                            <div
                                                                                                key={mediaId}
                                                                                                className="relative flex-shrink-0 w-24 h-14 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-black cursor-pointer group"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setViewingMedia(media);
                                                                                                }}
                                                                                                title={`Visualizar: ${media.title}`}
                                                                                            >
                                                                                                <img
                                                                                                    src={getThumbnailUrl(media)}
                                                                                                    alt={media.title}
                                                                                                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                                                                                                />
                                                                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                                                                                                    <Eye className="h-4 w-4 text-white drop-shadow-sm" />
                                                                                                </div>
                                                                                                {/* Tipo icon tiny */}
                                                                                                <div className="absolute bottom-0.5 right-0.5 bg-black/60 rounded px-1 py-0.5">
                                                                                                    {media.type === 'video' ? <Video className="h-2 w-2 text-white" /> : <Image className="h-2 w-2 text-white" />}
                                                                                                </div>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* Informações da Campanha */}
                                                                        <div className="grid grid-cols-2 gap-2">
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                                                                                    <Clock className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Período</span>
                                                                                </div>
                                                                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                                    {format(new Date(campaign.start_date), 'dd/MM/yy')} - {format(new Date(campaign.end_date), 'dd/MM/yy')}
                                                                                </p>
                                                                            </div>
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                                                                                    <Eye className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Impressões</span>
                                                                                </div>
                                                                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                                    {campaign.total_impressions ? (campaign.total_impressions / 1000).toFixed(1) + 'k' : '-'}
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {/* Comentário do admin (se houver) */}
                                                                        {campaign.admin_comment && (
                                                                            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2.5 border border-orange-200">
                                                                                <div className="flex items-center gap-1.5 text-orange-600 mb-1">
                                                                                    <AlertCircle className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Comentário Watch</span>
                                                                                </div>
                                                                                <p className="text-xs text-orange-800 dark:text-orange-300">
                                                                                    {campaign.admin_comment}
                                                                                </p>
                                                                            </div>
                                                                        )}

                                                                        {/* Ações - Desabilitadas para campanhas do banco por enquanto */}
                                                                        <div className="flex gap-2 pt-1">
                                                                            {(() => {
                                                                                const isEditable = campaign.status !== 'active';
                                                                                return (
                                                                                    <>
                                                                                        <Button
                                                                                            variant="outline"
                                                                                            size="sm"
                                                                                            disabled={!isEditable}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (isEditable) handleEditUrbanCampaign(campaign);
                                                                                            }}
                                                                                            className={`flex-1 h-8 text-xs gap-1.5 ${isEditable
                                                                                                ? 'hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200'
                                                                                                : 'opacity-50 cursor-not-allowed text-gray-400 bg-gray-50'
                                                                                                }`}
                                                                                        >
                                                                                            <Pencil className="h-3.5 w-3.5" />
                                                                                            Editar
                                                                                        </Button>
                                                                                        <Button
                                                                                            variant="outline"
                                                                                            size="sm"
                                                                                            disabled={!isEditable}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (isEditable) handleDeleteUrbanCampaign(campaign);
                                                                                            }}
                                                                                            className={`flex-1 h-8 text-xs gap-1.5 ${isEditable
                                                                                                ? 'hover:bg-red-50 hover:text-red-700 hover:border-red-200'
                                                                                                : 'opacity-50 cursor-not-allowed text-gray-400 bg-gray-50'
                                                                                                }`}
                                                                                        >
                                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                                            Excluir
                                                                                        </Button>
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                            <div className="flex-1 h-8 text-xs text-gray-400 flex items-center justify-center bg-gray-100 rounded-lg">
                                                                                <span>Criada em {format(new Date(campaign.created_at), 'dd/MM/yy HH:mm')}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Seção: Lista de Campanhas Venue - Colapsável (Somente se não for urban) */}
                                {adsServiceType !== 'urban' && (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                        <div
                                            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                            onClick={() => setIsCampaignsListExpanded(!isCampaignsListExpanded)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Store className="h-4 w-4 text-orange-600" />
                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Campanhas Venue
                                                </span>
                                                <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1.5 py-0">
                                                    {loadingVenueCampaigns ? '...' : venueCampaigns.length}
                                                </Badge>
                                            </div>
                                            {isCampaignsListExpanded ? (
                                                <ChevronUp className="h-4 w-4 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-gray-400" />
                                            )}
                                        </div>

                                        <div className={`transition-all duration-300 overflow-hidden ${isCampaignsListExpanded ? 'max-h-none' : 'max-h-0'}`}>
                                            <div className="overflow-y-auto max-h-[350px] px-3 pb-4 space-y-2">
                                                {loadingVenueCampaigns ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-orange-500" />
                                                        <p className="text-sm font-medium">Carregando campanhas...</p>
                                                    </div>
                                                ) : venueCampaigns.length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        <Store className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                                        <p className="text-sm font-medium">Nenhuma campanha Venue criada</p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            Crie sua primeira campanha selecionando um parceiro
                                                        </p>
                                                    </div>
                                                ) : (
                                                    venueCampaigns.map((campaign) => {
                                                        const isExpanded = expandedCampaignId === campaign.id;
                                                        const statusConfig = getStatusConfig(campaign.ads_status);

                                                        return (
                                                            <div
                                                                key={campaign.id}
                                                                className={`border rounded-lg overflow-hidden transition-all ${isExpanded
                                                                    ? 'border-orange-300 bg-orange-50/50 dark:bg-orange-900/20'
                                                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                                                    }`}
                                                            >
                                                                {/* Header da Campanha */}
                                                                <div
                                                                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                                                    onClick={() => setExpandedCampaignId(isExpanded ? null : campaign.id)}
                                                                >
                                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${campaign.ads_status === 'active' ? 'bg-green-500' :
                                                                            campaign.ads_status === 'pending' || campaign.ads_status === 'inactive' ? 'bg-yellow-500' :
                                                                                campaign.ads_status === 'correction' ? 'bg-orange-500' :
                                                                                    campaign.ads_status === 'approved' ? 'bg-green-500' :
                                                                                        campaign.ads_status === 'ended' ? 'bg-gray-400' :
                                                                                            campaign.ads_status === 'rejected' ? 'bg-red-500' : 'bg-gray-400'
                                                                            }`} />
                                                                        <div className="min-w-0 flex-1">
                                                                            <h4 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                                                                {campaign.campaign_name}
                                                                            </h4>
                                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                                <span className="text-[10px] text-gray-500">
                                                                                    {campaign.media_ids?.length || 0} mídia{(campaign.media_ids?.length || 0) !== 1 ? 's' : ''}
                                                                                </span>
                                                                                {campaign.venue_partner && (
                                                                                    <>
                                                                                        <span className="text-gray-300">•</span>
                                                                                        <span className="text-[10px] text-orange-600 truncate max-w-[120px]">
                                                                                            {campaign.venue_partner.venue_name}
                                                                                        </span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Badge className={`text-[10px] px-2 py-0.5 border ${statusConfig.color}`}>
                                                                            {statusConfig.label}
                                                                        </Badge>
                                                                        {isExpanded ? (
                                                                            <ChevronUp className="h-4 w-4 text-gray-400" />
                                                                        ) : (
                                                                            <ChevronDown className="h-4 w-4 text-gray-400" />
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Conteúdo expandido */}
                                                                <div className={`transition-all duration-200 overflow-hidden ${isExpanded ? 'max-h-none' : 'max-h-0'}`}>
                                                                    <div className="px-3 pb-3 pt-0 space-y-3">
                                                                        {/* Comentário Watch (Correção ou Rejeição) */}
                                                                        {(campaign.ads_status === 'correction' || campaign.ads_status === 'rejected') && campaign.admin_comment && !closedNotifications.includes(campaign.id) && (
                                                                            <div className={`rounded-lg p-3 border mb-2 relative group ${campaign.ads_status === 'rejected'
                                                                                ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-900/30'
                                                                                : 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-900/30'
                                                                                }`}>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setClosedNotifications(prev => [...prev, campaign.id]);
                                                                                    }}
                                                                                    className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 transition-colors"
                                                                                >
                                                                                    <X className={`h-3 w-3 ${campaign.ads_status === 'rejected' ? 'text-red-600' : 'text-orange-600'}`} />
                                                                                </button>
                                                                                <div className="flex items-start gap-2 pr-4">
                                                                                    <AlertCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${campaign.ads_status === 'rejected' ? 'text-red-600' : 'text-orange-600'
                                                                                        }`} />
                                                                                    <div>
                                                                                        <p className={`text-xs font-bold mb-1 ${campaign.ads_status === 'rejected' ? 'text-red-700 dark:text-red-300' : 'text-orange-700 dark:text-orange-300'
                                                                                            }`}>
                                                                                            {campaign.ads_status === 'correction' ? 'Ação Necessária:' : 'Motivo da Rejeição:'}
                                                                                        </p>
                                                                                        <p className={`text-xs ${campaign.ads_status === 'rejected' ? 'text-red-600 dark:text-red-200' : 'text-orange-600 dark:text-orange-200'
                                                                                            }`}>
                                                                                            {campaign.admin_comment}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {/* Descrição */}
                                                                        {campaign.campaign_description && (
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                                    {campaign.campaign_description}
                                                                                </p>
                                                                            </div>
                                                                        )}

                                                                        {/* Informações da Campanha */}
                                                                        <div className="grid grid-cols-2 gap-2">
                                                                            {/* Período */}
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                                                                                    <Clock className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Período</span>
                                                                                </div>
                                                                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                                                    {format(new Date(campaign.start_date + 'T00:00:00'), 'dd/MM/yy')} - {format(new Date(campaign.end_date + 'T00:00:00'), 'dd/MM/yy')}
                                                                                </p>
                                                                            </div>

                                                                            {/* Parceiro */}
                                                                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2.5">
                                                                                <div className="flex items-center gap-1.5 text-gray-500 mb-1">
                                                                                    <Store className="h-3 w-3" />
                                                                                    <span className="text-[10px] uppercase font-medium">Parceiro</span>
                                                                                </div>
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {campaign.venue_partner ? (
                                                                                        <Badge className="bg-orange-100 text-orange-700 text-[9px] px-1.5 py-0">
                                                                                            {campaign.venue_partner.venue_city}
                                                                                        </Badge>
                                                                                    ) : (
                                                                                        <span className="text-xs text-gray-400">-</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Ações */}
                                                                        <div className="flex gap-2 pt-1">
                                                                            {(() => {
                                                                                const isEditable = ['pending', 'inactive', 'rejected', 'correction'].includes(campaign.ads_status);
                                                                                return (
                                                                                    <Button
                                                                                        variant="outline"
                                                                                        size="sm"
                                                                                        disabled={!isEditable}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            if (isEditable) handleEditVenueCampaign(campaign);
                                                                                        }}
                                                                                        className={`flex-1 h-8 text-xs gap-1.5 ${isEditable
                                                                                            ? 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
                                                                                            : 'opacity-50 cursor-not-allowed text-gray-400'
                                                                                            }`}
                                                                                    >
                                                                                        <Pencil className="h-3.5 w-3.5" />
                                                                                        Editar
                                                                                    </Button>
                                                                                );
                                                                            })()}
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleDeleteVenueCampaign(campaign);
                                                                                }}
                                                                                className="flex-1 h-8 text-xs gap-1.5 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                                                                            >
                                                                                <Trash2 className="h-3.5 w-3.5" />
                                                                                Excluir
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Placeholder para futura seção de Métricas */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden opacity-60">
                                    <div className="flex items-center justify-between p-3">
                                        <div className="flex items-center gap-2">
                                            <Sparkles className="h-4 w-4 text-gray-400" />
                                            <span className="text-sm font-medium text-gray-400">
                                                Métricas
                                            </span>
                                            <Badge className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0">
                                                Em breve
                                            </Badge>
                                        </div>
                                        <ChevronDown className="h-4 w-4 text-gray-300" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer - condicional por tab */}
                    {activeTab === 'create' && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800 flex justify-between items-center flex-shrink-0">
                            {editingVenueCampaign ? (
                                /* Modo de Edição de Campanha Venue */
                                <>
                                    <Button variant="outline" onClick={handleCancelVenueCampaignEdit}>
                                        ← Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleSaveVenueCampaignEdit}
                                        disabled={!packName.trim() || !startDate || !endDate || isSubmitting}
                                        className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white min-w-[180px]"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                Salvando...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                                Salvar Alterações
                                            </>
                                        )}
                                    </Button>
                                </>
                            ) : editingUrbanCampaign ? (
                                /* Modo de Edição de Campanha Urban */
                                <>
                                    <Button variant="outline" onClick={handleCancelUrbanCampaignEdit}>
                                        ← Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleSaveUrbanCampaignEdit}
                                        disabled={!packName.trim() || !startDate || !endDate || selectedCities.length === 0 || isSubmitting}
                                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white min-w-[180px]"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                Salvando...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                                Salvar Alterações
                                            </>
                                        )}
                                    </Button>
                                </>
                            ) : editingCampaign ? (
                                /* Modo de Edição de Campanha Mock/Urban */
                                <>
                                    <Button variant="outline" onClick={handleCancelEdit}>
                                        ← Cancelar
                                    </Button>
                                    <Button
                                        onClick={handleSaveEdit}
                                        disabled={!packName.trim() || selectedCities.length === 0 || !startDate || !endDate || isSubmitting}
                                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white min-w-[180px]"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                Salvando...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                                Salvar Alterações
                                            </>
                                        )}
                                    </Button>
                                </>
                            ) : step === 1 ? (
                                /* Step 1: Escolha do tipo de veiculação */
                                <>
                                    <Button variant="outline" onClick={onClose}>
                                        Cancelar
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            if (adsServiceType === 'urban') {
                                                setHasChosenService(true);
                                                setStep(2);
                                            } else if (adsServiceType === 'venue') {
                                                // ADS Venue - avança para tela de parceiros
                                                setHasChosenService(true);
                                                setStep(2);
                                            }
                                        }}
                                        disabled={!adsServiceType}
                                        className={`min-w-[180px] text-white transition-all duration-300 ${adsServiceType === 'urban'
                                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-[0_0_15px_rgba(168,85,247,0.4)]'
                                            : adsServiceType === 'venue'
                                                ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-[0_0_15px_rgba(251,146,60,0.4)]'
                                                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                                            }`}
                                    >
                                        Continuar
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </>
                            ) : step === 2 ? (
                                /* Step 2: Seleção de mídias (para ambos Urban e Venue) */
                                <>
                                    <Button variant="outline" onClick={() => { setStep(1); setHasChosenService(false); setSelectedPartner(null); }}>
                                        ← Voltar
                                    </Button>
                                    <Button
                                        onClick={() => setStep(3)}
                                        disabled={!canProceedToStep2}
                                        className={`min-w-[140px] text-white ${adsServiceType === 'venue'
                                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                                            : adsServiceType === 'urban'
                                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                                                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                                            }`}
                                    >
                                        {adsServiceType === 'venue' ? 'Escolher Parceiro' : 'Continuar'}
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </>
                            ) : step === 3 && adsServiceType === 'venue' ? (
                                /* Step 3 (Venue): Lista de parceiros */
                                <>
                                    <Button variant="outline" onClick={() => { setStep(2); setSelectedPartner(null); }}>
                                        ← Voltar
                                    </Button>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Info className="h-4 w-4" />
                                        <span>Clique em um parceiro para selecionar</span>
                                    </div>
                                </>
                            ) : (
                                /* Step 3 (Urban) ou Step 4 (Venue): Configuração */
                                <>
                                    <Button variant="outline" onClick={() => setStep(adsServiceType === 'venue' ? 3 : 2)}>
                                        ← Voltar
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={!canSubmit || isSubmitting}
                                        className={`min-w-[180px] ${isExternalCampaign
                                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white'
                                            : adsServiceType === 'urban'
                                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                                                : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                {isExternalCampaign ? 'Enviando...' : 'Criando...'}
                                            </>
                                        ) : (
                                            <>
                                                {isExternalCampaign ? (
                                                    <>
                                                        <CheckCircle2 className="mr-2 h-4 w-4" />
                                                        Enviar campanha para aprovação
                                                    </>
                                                ) : (
                                                    <>
                                                        <Package className="mr-2 h-4 w-4" />
                                                        Criar Campanha Ads
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'campaigns' && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800 flex justify-between items-center flex-shrink-0">
                            <Button variant="outline" onClick={onClose}>
                                Fechar
                            </Button>
                            <Button
                                onClick={() => setActiveTab('create')}
                                className={`text-white ${adsServiceType === 'urban'
                                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                                    }`}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Nova Campanha
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog >

            {/* Diálogo de Confirmação de Exclusão */}
            < AlertDialog open={!!campaignToDelete
            } onOpenChange={(open) => !open && cancelDeleteCampaign()}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="h-5 w-5 text-red-600" />
                            </div>
                            <AlertDialogTitle className="text-lg">
                                Excluir campanha?
                            </AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-gray-600">
                            Você está prestes a excluir a campanha{' '}
                            <span className="font-semibold text-gray-900">"{campaignToDelete?.name}"</span>.
                            <br /><br />
                            Esta ação não pode ser desfeita. A campanha será removida permanentemente e todos os dados associados serão perdidos.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                        <AlertDialogCancel
                            onClick={cancelDeleteCampaign}
                            className="border-gray-300"
                        >
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteCampaign}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Sim, excluir campanha
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog >

            {/* Diálogo de Confirmação de Exclusão de Campanha Urban */}
            < AlertDialog open={!!urbanCampaignToDelete} onOpenChange={(open) => !open && cancelDeleteUrbanCampaign()}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="h-5 w-5 text-red-600" />
                            </div>
                            <AlertDialogTitle className="text-lg">
                                Excluir campanha Urban?
                            </AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-gray-600">
                            Você está prestes a excluir a campanha{' '}
                            <span className="font-semibold text-gray-900">"{urbanCampaignToDelete?.campaign_name}"</span>.
                            <br /><br />
                            Esta ação não pode ser desfeita. A campanha será removida permanentemente do banco de dados.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                        <AlertDialogCancel
                            onClick={cancelDeleteUrbanCampaign}
                            className="border-gray-300"
                        >
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteUrbanCampaign}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Sim, excluir campanha
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog >

            {/* Diálogo de Confirmação de Exclusão de Campanha Venue */}
            < AlertDialog open={!!venueCampaignToDelete} onOpenChange={(open) => !open && cancelDeleteVenueCampaign()}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <Trash2 className="h-5 w-5 text-red-600" />
                            </div>
                            <AlertDialogTitle className="text-lg">
                                Excluir campanha Venue?
                            </AlertDialogTitle>
                        </div>
                        <AlertDialogDescription className="text-gray-600">
                            Você está prestes a excluir a campanha{' '}
                            <span className="font-semibold text-gray-900">"{venueCampaignToDelete?.campaign_name}"</span>.
                            <br /><br />
                            Esta ação não pode ser desfeita. A campanha será removida permanentemente do banco de dados.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                        <AlertDialogCancel
                            onClick={cancelDeleteVenueCampaign}
                            className="border-gray-300"
                        >
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteVenueCampaign}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Sim, excluir campanha
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog >

            {/* Modal de Visualização de Mídia (ADS Urban e Minhas Campanhas) */}
            <Dialog open={!!viewingMedia} onOpenChange={(open) => !open && setViewingMedia(null)}>

                <DialogContent className="w-screen h-screen max-w-none m-0 p-0 bg-transparent border-none shadow-none flex flex-col items-center justify-center focus:outline-none outline-none z-[100]">

                    <div className="w-full h-full flex items-center justify-center p-4 sm:p-8" onClick={() => setViewingMedia(null)}>
                        {viewingMedia && (
                            <div
                                className="relative flex items-center justify-center max-w-full max-h-full group/media"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Botão Fechar Unificado (Sobre a Mídia) */}
                                <button
                                    onClick={() => setViewingMedia(null)}
                                    className="absolute top-4 right-4 z-[70] p-2 rounded-full bg-black/50 text-white hover:bg-red-500/90 backdrop-blur-md transition-all duration-300 hover:scale-110 border border-white/10 shadow-lg opacity-0 group-hover/media:opacity-100 focus:opacity-100"
                                    title="Fechar"
                                >
                                    <X className="h-5 w-5" />
                                </button>

                                {(viewingMedia.type === 'video' || viewingMedia.url.includes('youtube') || viewingMedia.url.includes('vimeo')) ? (
                                    (() => {
                                        const youtubeMatch = viewingMedia.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
                                        if (youtubeMatch) {
                                            return (
                                                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 w-[85vw] max-w-6xl aspect-video bg-black">
                                                    <iframe
                                                        src={`https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`}
                                                        className="w-full h-full"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                </div>
                                            );
                                        }
                                        return (
                                            <video
                                                controls
                                                className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl border border-white/10 bg-black"
                                                autoPlay
                                            >
                                                <source src={viewingMedia.url} type="video/mp4" />
                                                Seu navegador não suporta a tag de vídeo.
                                            </video>
                                        );
                                    })()
                                ) : (
                                    <img
                                        src={viewingMedia.url}
                                        alt={viewingMedia.title}
                                        className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl drop-shadow-2xl select-none"
                                    />
                                )}

                                {/* Legenda Flutuante */}
                                <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center pointer-events-none transform transition-all duration-500 w-full px-4">
                                    <div className="inline-block bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-6 py-2.5 shadow-xl">
                                        <h3 className="font-medium text-base text-white tracking-wide">{viewingMedia.title}</h3>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal do Carrossel de Fotos */}
            < Dialog open={photoCarouselOpen} onOpenChange={closePhotoCarousel} >
                <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-black/95 border-none overflow-hidden">
                    {/* Header do carrossel */}
                    <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                                    <Store className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-white font-semibold text-sm">{carouselPartnerName}</h3>
                                    <p className="text-white/60 text-xs">
                                        Foto {carouselIndex + 1} de {carouselPhotos.length}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Botão de Zoom */}
                                <button
                                    onClick={() => setIsZoomed(!isZoomed)}
                                    className={`p-2 rounded-full transition-all duration-200 ${isZoomed
                                        ? 'bg-orange-500 text-white'
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                        }`}
                                    title={isZoomed ? 'Diminuir zoom' : 'Aumentar zoom'}
                                >
                                    <ZoomIn className="h-5 w-5" />
                                </button>
                                {/* Botão de Fechar */}
                                <button
                                    onClick={closePhotoCarousel}
                                    className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Imagem principal */}
                    <div
                        className={`relative w-full h-[70vh] flex items-center justify-center overflow-hidden transition-all duration-300 ${isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
                            }`}
                        onClick={() => setIsZoomed(!isZoomed)}
                    >
                        {carouselPhotos[carouselIndex] && (
                            <img
                                src={carouselPhotos[carouselIndex]}
                                alt={`${carouselPartnerName} - Foto ${carouselIndex + 1}`}
                                className={`max-w-full max-h-full object-contain transition-transform duration-300 ${isZoomed ? 'scale-150' : 'scale-100'
                                    }`}
                            />
                        )}
                    </div>

                    {/* Navegação por setas */}
                    {carouselPhotos.length > 1 && (
                        <>
                            {/* Seta esquerda */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateCarousel('prev');
                                }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-200 backdrop-blur-sm group"
                            >
                                <ChevronLeft className="h-6 w-6 group-hover:scale-110 transition-transform" />
                            </button>

                            {/* Seta direita */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateCarousel('next');
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-200 backdrop-blur-sm group"
                            >
                                <ChevronRight className="h-6 w-6 group-hover:scale-110 transition-transform" />
                            </button>
                        </>
                    )}

                    {/* Indicadores de posição (dots) */}
                    {carouselPhotos.length > 1 && (
                        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-20">
                            {carouselPhotos.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCarouselIndex(index);
                                        setIsZoomed(false);
                                    }}
                                    className={`w-2 h-2 rounded-full transition-all duration-200 ${index === carouselIndex
                                        ? 'bg-orange-500 w-6'
                                        : 'bg-white/40 hover:bg-white/60'
                                        }`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Thumbnails na parte inferior */}
                    {carouselPhotos.length > 1 && (
                        <div className="absolute bottom-12 left-0 right-0 px-4 z-10">
                            <div className="flex justify-center gap-2 overflow-x-auto pb-2">
                                {carouselPhotos.map((photo, index) => (
                                    <button
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCarouselIndex(index);
                                            setIsZoomed(false);
                                        }}
                                        className={`w-14 h-10 rounded-md overflow-hidden flex-shrink-0 transition-all duration-200 ${index === carouselIndex
                                            ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-black scale-110'
                                            : 'opacity-50 hover:opacity-80'
                                            }`}
                                    >
                                        <img
                                            src={photo}
                                            alt={`Thumbnail ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog >

            {/* Modal de seleção de tempo de exibição (ADS Venue) */}
            <Dialog open={displayTimeModalOpen} onOpenChange={(open) => !open && handleCancelDisplayTime()}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-orange-500" />
                            Tempo de Exibição
                        </DialogTitle>
                        <DialogDescription>
                            Escolha por quanto tempo esta mídia será exibida na tela do ponto de exibição
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 py-4">
                        {[
                            { seconds: 15, label: '15 segundos' },
                            { seconds: 20, label: '20 segundos' },
                            { seconds: 30, label: '30 segundos' },
                            { seconds: 60, label: '60 segundos' },
                        ].map((option) => (
                            <button
                                key={option.seconds}
                                onClick={() => handleConfirmDisplayTime(option.seconds)}
                                className="flex flex-col items-center justify-center gap-1 p-4 rounded-xl border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 transition-all duration-200 group"
                            >
                                <span className="text-2xl font-bold text-gray-700 group-hover:text-orange-600">
                                    {option.seconds}s
                                </span>
                                <span className="text-xs text-gray-500 group-hover:text-orange-500">
                                    {option.label}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="flex items-end justify-between gap-4 pt-2 border-t border-gray-100">
                        <p className="text-[11px] text-gray-400 leading-relaxed max-w-[280px]">
                            <span className="font-medium text-gray-500">Nota:</span> O valor do investimento publicitário é calculado proporcionalmente ao tempo de exibição selecionado.
                        </p>
                        <Button variant="outline" onClick={handleCancelDisplayTime} className="shrink-0">
                            Cancelar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default WatchAdsModal;
